<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import {allowedBankID, formatAmount, formatPercents, formatYearsString, getApiBaseUrl, getLangToSet, maskPhoneNumber, setButtonContentDefault, setButtonContentLoading, showErrorMessage, showSuccessMessage} from '@/helper';
import {authData, checkForBankIdLogin, generate2faCode, getBankIdUrl, getLoggedUser} from '@/services/AuthService';
import DynamicForm from '@/views/DynamicForm.vue';
import DocumentsListMinimalComponent from '@/components/documents/DocumentsListMinimalComponent.vue';
import {Capacitor} from '@capacitor/core';
import {Browser} from '@capacitor/browser';
import CustomDropzone from '@/components/forms/CustomDropzone.vue';
import {ErrorMessage, Field, Form} from 'vee-validate';

import {useI18n} from 'vue-i18n';
import userService from '@/services/UserService';
import Select from '@/components/forms/Select.vue';
import Multiselect from '@vueform/multiselect';
import OnboardingHeader from '@/components/layouts/OnboardingHeader.vue';
import OnboardingFooter from '@/components/layouts/OnboardingFooter.vue';
import LineChart from '@/components/echarts/LineChart.vue';
import {createConfirmDialog} from 'vuejs-confirm-dialog';
import DocumentDetailModal from '@/components/modals/DocumentDetailModal.vue';
import {getTenantData} from '@/services/TenantService';
import BSTooltip from '@/components/tooltips/BSTooltip.vue';
import interactionFormService from '@/services/InteractionFormService';
import DocumentService from '@/services/DocumentService';
import FormService from '@/services/FormService';
import InvestmentQuestionnaireResultsModal from "@/components/modals/InvestmentQuestionnaireResultsModal.vue";
import InvestmentQuestionnaireModal from "@/components/modals/InvestmentQuestionnaireModal.vue";
import InvestmentQuestionnaireService from "@/services/InvestmentQuestionnaireService";
import AmlQuestionnaireResultsModal from "@/components/modals/AmlQuestionnaireResultsModal.vue";
import CurrencyInput from '@/components/forms/CurrencyInput.vue';
import AppAnnouncements from '@/components/AppAnnouncements.vue';

const {t} = useI18n();
const locale = getLangToSet();
const props = defineProps({
  assistedOnboarding: Object
});

const isBidli = window.location.hostname.includes('biginvest');

// Differentiate between biginvest.cz and biginvest.sk for the portfolia link
const portfoliaUrl = (() => {
  const hostname = window.location.hostname;
  if (hostname.includes('biginvest.cz')) return 'https://www.biginvest.cz/portfolia';
  if (hostname.includes('biginvest.sk')) return 'https://www.biginvest.sk/portfolia';
  return 'https://in-equity.cz/nase-produkty';
})();
const loggedUser = ref(null);
const selectedStrategy = ref(null);
const selectedIdentificationMethod = ref(null);
const availableMethods = [
  {label: t('clientOnboardingOverlay.document_upload'), value: 'DOCUMENT'},
];
const selectedIdentificationDocumentTypes = ref({0: null, 1: null});
const availableDocumentTypes = ref([[], []]);
const allAvailableDocumentTypes = [
  {label: t('clientOnboardingOverlay.ID_CARD'), value: 'ID_CARD'},
  {label: t('clientOnboardingOverlay.PASSPORT'), value: 'PASSPORT'},
  {label: t('clientOnboardingOverlay.BIRTH_CERTIFICATE'), value: 'BIRTH_CERTIFICATE'},
  {label: t('clientOnboardingOverlay.DRIVER_LICENSE'), value: 'DRIVER_LICENSE'},
  {label: t('clientOnboardingOverlay.RESIDENCE_PERMIT'), value: 'RESIDENCE_PERMIT'},
];
const appName = ref(isBidli ? 'B.I.G. Invest' : 'IN Equity');
const interactionFormsID = ref(null);
const steps = ref([{type: 'start', title: t('clientOnboardingOverlay.requirements_to_processed_new', {appName: appName.value} )}]);
const step = ref({});
const stepIndex = ref(0);
const aggregatedFormStepIndex = ref(0);
const aggregatedFormsCount = ref(0);
const emit = defineEmits(['onboardingDone']);
const addedFiles = ref([]);
const bankIDIdentificationDone = ref(false);
const bankIDIdentificationProcessing = ref(false);
const codeSent = ref(false);
const codeSentLoading = ref(false);
const codeSentAgain = ref(false);
const codeRequestInterval = ref(0);
const errorMessage = ref('');
const showBankAccount = ref(false);
const goBackToSummaryAfterStepSubmit = ref(false);
const hiddenDropzone = ref({
  front_page: false,
  back_page: false,
  PASSPORT: false,
  BIRTH_CERTIFICATE: false,
  DRIVER_LICENSE: false,
  RESIDENCE_PERMIT: false,
});
const showVerification = ref(false);
const showAnotherInformation = ref(false);
// IMA-486: přílohy nahrané poradcem/back office přímo k tomuto onboardingu (ne klientem)
const showAddAttachment = ref(false);
const interactionFormAttachments = ref([]);
const attachmentsDropzoneRef = ref(null);

// IMA-500: "Podepsat další dokumenty" - poradce může (jen pokud to nastavení kategorie
// povoluje) přiložit k podpisu už dříve vytvořené, ale nepodepsané formuláře klienta
// (Form ve stavu WAITING, založené přes administraci - CreateEditFormModal).
const additionalDocumentsEnabled = ref(false);
const interactionFormClientId = ref(null);
const showAddForms = ref(false);
const availableWaitingForms = ref([]);
const availableWaitingFormsLoading = ref(false);
// FormController::getAll() vrací syrové sloupce modelu Form - ten nemá vlastní
// sloupec "name", název formuláře je uložený v data.name (viz FormTrait::getFormTemplateName()
// na BE). Bez fallbacku by multiselect nabízel položky s prázdným popiskem.
const availableWaitingFormsOptions = computed(() =>
  availableWaitingForms.value.map(f => ({ value: f.id, label: f.data?.name || `Formulář #${f.id}` }))
);

function loadAvailableWaitingForms() {
  if (!interactionFormClientId.value || availableWaitingFormsLoading.value) {
    return;
  }
  availableWaitingFormsLoading.value = true;
  FormService.getAll(interactionFormClientId.value).then(forms => {
    availableWaitingForms.value = (forms ?? []).filter(f => f.state === 'WAITING');
  }).finally(() => {
    availableWaitingFormsLoading.value = false;
  });
}


// IMA-487: v AML kroku (a obecně u formulářů) může být jednotlivá otázka v JSON označená
// 'visible_to_client: false' - poradce/back office ji vidí a vyplňuje, klientovi se nezobrazí.
// advisor_only skryje celý krok - tohle filtruje jen jednotlivé otázky uvnitř kroku, který
// klient jinak vidí.
const amlVisibleStepData = computed(() => {
  const content = step.value?.data?.content;
  if (!content || props.assistedOnboarding) {
    return step.value?.data;
  }
  return {
    ...step.value.data,
    content: content.map(fieldset => ({
      ...fieldset,
      questions: (fieldset.questions ?? []).filter(question => question.visible_to_client !== false),
    })),
  };
});
const availableStrategies = ref([]);
const bestPerformanceStrategy = ref(null);
const performance = ref(0);
const tenantTheme = ref({});

const monthlyInvestment = ref(0);
const finalInvestmentValue = ref(0);
const totalInvestedAmount = ref(0);
const paymentInformations = ref({});
const currency = ref('CZK');

const summaryDataOrder = ref([
  'client_complex_form',
  'calculation',
  'address',
  'bank_account',
]);

/**
 * Vrátí dvojici CZ/SK hodnot v pořadí podle jazyka aplikace – v SK verzi je
 * Slovensko první, v CZ verzi Česko. Mění se jen pořadí v selectu; ukládaná
 * hodnota (přeložený název) zůstává stejná, takže to nemá vliv na uložená data ani Bidli.
 *
 * @param {string} czech  Popisek pro Česko (stát nebo občanství)
 * @param {string} slovak Popisek pro Slovensko (stát nebo občanství)
 * @returns {string[]}
 */
function homeCountriesFirst(czech, slovak) {
  return locale === 'sk' ? [slovak, czech] : [czech, slovak];
}

const countryOriginSelectOptions = [
  ...homeCountriesFirst(t('clientOnboardingOverlay.czech_republic'), t('clientOnboardingOverlay.slovak_republic')),
  t('clientOnboardingOverlay.poland'),
  t('clientOnboardingOverlay.ukraine'),
  t('clientOnboardingOverlay.hungary'),
  t('clientOnboardingOverlay.switzerland'),
  t('clientOnboardingOverlay.germany'),
  t('clientOnboardingOverlay.liechtenstein'),
  t('clientOnboardingOverlay.serbia'),
  // IMA-525: Kazachstán – ASO ukládá přeložený název (kvůli souhrnu, dokumentům a Bidli)
  t('clientOnboardingOverlay.kazakhstan')
];

const citizenshipSelectOptions = [
  ...homeCountriesFirst(t('clientOnboardingOverlay.czech'), t('clientOnboardingOverlay.slovak')),
  t('clientOnboardingOverlay.polish'),
  t('clientOnboardingOverlay.ukrainian'),
  t('clientOnboardingOverlay.hungarian'),
  t('clientOnboardingOverlay.austrian'),
  t('clientOnboardingOverlay.swiss'),
  t('clientOnboardingOverlay.german'),
  t('clientOnboardingOverlay.liechtenstein_citizenship'),
  t('clientOnboardingOverlay.serbian'),
  // IMA-525: kazašské občanství
  t('clientOnboardingOverlay.kazakh')
];

const taxResidenceSelectOptions = [
  ...homeCountriesFirst(t('clientOnboardingOverlay.czech_republic'), t('clientOnboardingOverlay.slovak_republic')),
  t('clientOnboardingOverlay.poland'),
  t('clientOnboardingOverlay.ukraine'),
  t('clientOnboardingOverlay.hungary'),
  t('clientOnboardingOverlay.switzerland'),
  t('clientOnboardingOverlay.germany'),
  t('clientOnboardingOverlay.liechtenstein'),
  t('clientOnboardingOverlay.serbia'),
  // IMA-525: Kazachstán – ASO ukládá přeložený název (kvůli souhrnu, dokumentům a Bidli)
  t('clientOnboardingOverlay.kazakhstan')
];

const targetAmountOptions = {
  '100000': '100000',
  '500000': '500000',
  '1000000': '1000000',
  '5000000': '5000000',
  '20000000': '20000000',
  '100000000': '100000000',
  '300000000': '300000000',
};

const initialDepositOptions = ref(null);
const regularDepositOptions = ref(null);

const calculationChartDataSeries = ref([
  {
    title: t('clientOnboardingOverlay.deposited_funds'),
    data: [820, 932, 901, 934, 1290, 1330, 1320],
  }, {
    title: t('clientOnboardingOverlay.investment_development'),
    data: [1245, 454, 753, 263, 164, 753, 163],
  }
]);
const calculationChartDataCategories = ref([1, 2, 3, 4, 5, 6, 7]);

const interactionFormState = ref('');

const isAssistedOnboarding = ref(false);

const defaultOnboardingSettings = ref([]);
// Map of currency → settings (initial/regular deposit min/max/step) for all available
// currencies, returned by the backend as default_onboarding_settings_by_currency.
// Used to rebuild slider options when the user switches currency in the calculator.
const defaultOnboardingSettingsByCurrency = ref({});

// Settings for the currently selected currency (initial/regular deposit min/max/step).
// Falls back to the default settings when per-currency settings are not yet loaded.
const currentCurrencySettings = computed(() =>
  defaultOnboardingSettingsByCurrency.value[currency.value] ?? defaultOnboardingSettings.value
);

// EUR currency uses a lower investment goal minimum (5 000 EUR) regardless of domain
const investmentGoalMin = computed(() => {
  if (step.value?.data?.parameters?.general?.currency === 'EUR') {
    return 5000;
  }
  return 100000;
});

const showAssistedOnboardingIntro = ref(false);

// True once the advisor has actually changed the meeting_records text from the generated value.
// When true, processCalculationStep() no longer regenerates generated_summary_text,
// and a warning is shown in the calculator that changes won't affect the meeting record.
const meetingRecordModified = ref(false);

// The last text the calculator injected into meeting_records. Used to detect whether
// the advisor has manually changed the text (comparison on navigate-away).
const lastGeneratedSummaryText = ref(null);

const introMeetingRecordsSummary = ref(null);

onMounted(() => {
  tenantTheme.value = localStorage.getItem('TENANT_THEME');
  getTenantData()
      .then((response) => {
        tenantTheme.value = response.theme;
      });

  performance.value = 0;

  refreshLoggedUser();

  if (allowedBankID()) {
    availableMethods.push({label: 'Bank ID', value: 'BANK_ID'});
  } else {
    selectedIdentificationMethod.value = 'DOCUMENT';
  }

  watch(addedFiles, () => {
    const files = addedFiles.value;
    const obj = {
      front_page: false,
      back_page: false,
    };
    files.forEach(file => {
      obj[file.entity] = true;
    });

    hiddenDropzone.value = {...obj};
  });

  watch(step, (newValue) => {
    if (step?.value?.type === 'bank_account') {
      showBankAccount.value = showBankAccountFn();
    }
  }, {deep: true});

  // flush: 'sync' ensures initialDepositOptions and regularDepositOptions are populated
  // synchronously before Vue re-renders the component. Without this, sliders that use
  // :data="initialDepositOptions" render with null data on the first paint, causing
  // vue-slider to fall back to its default range and reset the v-model value.
  watch(defaultOnboardingSettings, () => {
    buildSliderOptions(defaultOnboardingSettings.value);
    currency.value = defaultOnboardingSettings.value?.currency;
  }, {flush: 'sync'})
});

/**
 * Builds initialDepositOptions and regularDepositOptions from the given settings object.
 * Called on initial load (via the defaultOnboardingSettings watcher) and on currency
 * change (via setCurrency) so sliders always reflect the correct currency's min/max/step.
 */
function buildSliderOptions(settings) {
  if (!settings) return;

  const initDepositOptions = {};
  const regDepositOptions = {};

  const initDepositMin = settings.initial_deposit_min;
  const initDepositMax = settings.initial_deposit_max;
  const initDepositStep = settings.initial_deposit_step;

  const regDepositMin = settings.regular_deposit_min;
  const regDepositMax = settings.regular_deposit_max;
  const regDepositStep = settings.regular_deposit_step;

  for (let i = initDepositMin; i <= initDepositMax; i += initDepositStep) {
    initDepositOptions[i.toString()] = i.toString();
  }

  for (let i = regDepositMin; i <= regDepositMax; i === 500 ? i = i + 500 : i += regDepositStep) {
    regDepositOptions[i.toString()] = i.toString();
  }

  initialDepositOptions.value = initDepositOptions;
  regularDepositOptions.value = regDepositOptions;
}

function calculationUpdated() {
  recountCalculation();
}

function onModeChange() {
  // Use nextTick so sliders for the newly selected mode initialize their values
  // (and potentially clamp/snap to valid options) before recountCalculation reads them.
  nextTick(() => {
    recountCalculation();
  });
}

function getPerformance(strategy) {
  performance.value = strategy.performance ?? 0;
  recountCalculation();
}

function calculateMonthlyInvestment(finalAmount, annualRate, years) {
  if (!Number.isFinite(finalAmount) || !Number.isFinite(annualRate) || !Number.isFinite(years) || years <= 0) {
    return 0;
  }

  const months = years * 12;
  const monthlyRate = Math.pow(1 + annualRate, 1 / 12) - 1;

  if (Math.abs(monthlyRate) < 1e-9) {
    return finalAmount / months;
  }

  const growthFactor = Math.pow(1 + monthlyRate, months);
  return (finalAmount * monthlyRate) / (growthFactor - 1);
}

function recountCalculation() {

  let step = steps.value.find(st => st.type === 'calculation');
  let stepData = step?.data;
  if (!stepData) {
    return;
  }
  stepData.strategy_id = selectedStrategy.value.id;
  let durationInYears = stepData.parameters.general.duration;
  let performanceValue = performance.value;
  let initialDeposit = 0;
  let regularDeposit = 0;
  let comparePerformance = 0.03;

  // data.monthly_deposit
  if (stepData.mode === 'TARGET') {
    initialDeposit = 0;
    regularDeposit = calculateMonthlyInvestment(Number(stepData.parameters.target.amount), performanceValue, durationInYears);
    stepData.monthly_deposit = regularDeposit;
    monthlyInvestment.value = regularDeposit;
  } else {
    initialDeposit = Number(stepData.parameters.saving.initial_deposit);
    regularDeposit = Number(stepData.parameters.saving.regular_deposit);
  }

  recountChartData(initialDeposit, regularDeposit, durationInYears, performanceValue, comparePerformance, stepData);
}

function recountChartData(initialDeposit, regularDeposit, durationInYears, performance, comparePerformance, stepData) {
  let categories = [];
  let depositsAmount = [];
  let compareInvestmentAmount = [];
  let investmentAmount = [];

  let depositsTotal = 0;
  let investmentTotal = 0;
  let compareInvestmentTotal = 0;

  let monthlyPerformance = performance / 12;
  let monthlyComparePerformance = comparePerformance ? comparePerformance / 12 : 0;


  for (let year = 1; year <= durationInYears; year++) {
    categories.push(year);


    for (let month = 1; month <= 12; month++) {

      // check deposit
      const depositsThisMonth = (month === 1 && year === 1 ? initialDeposit : 0) + regularDeposit;
      depositsTotal += depositsThisMonth;

      // check performance
      investmentTotal += depositsThisMonth;
      investmentTotal = investmentTotal * (1 + monthlyPerformance);

      // check performance
      compareInvestmentTotal += depositsThisMonth;
      compareInvestmentTotal = compareInvestmentTotal * (1 + monthlyComparePerformance);

    }

    depositsAmount.push(depositsTotal);
    investmentAmount.push(investmentTotal);
    compareInvestmentAmount.push(compareInvestmentTotal);

  }

  let chartData = [
    {
      name: t('clientOnboardingOverlay.deposited_funds'),
      data: depositsAmount,
      itemStyle: {
        color: 'rgba(199,122,86,1)'
      }
    }, {
      name: t('clientOnboardingOverlay.expected_investment_value'),
      data: investmentAmount
    }
  ];
  if (comparePerformance) {
    chartData.push({
      name: t('clientOnboardingOverlay.expected_inflation_rate'),
      data: compareInvestmentAmount,
      itemStyle: {
        color: 'rgba(4,110,122,1)'
      }
    });
  }


  calculationChartDataCategories.value = categories;
  calculationChartDataSeries.value = chartData;

  stepData.final_amount = investmentTotal;
  stepData.deposits_amount = depositsTotal;


  finalInvestmentValue.value = investmentTotal;
  totalInvestedAmount.value = depositsTotal;
}

function refreshLoggedUser() {
  getLoggedUser().then(user => {
    loggedUser.value = user;
    if (user?.meta?.waiting_interaction_forms > 0 || props.assistedOnboarding) {
      getInteractionForm().then(form => {
        for (const step of form.steps) {
          if (!step.data) {
            step.data = {};
          }
          if (!step.data.correspondence_address || step.data.correspondence_address?.length === 0) {
            step.data.correspondence_address = {};
          }
          if (!step.data.domicile_address || step.data.domicile_address?.length === 0) {
            step.data.domicile_address = {};
          }
          if (step.type === 'aggregated_form') {
            aggregatedFormsCount.value = step.data?.content?.length;
          }
          // vue-slider uses string keys in :data options (e.g. '50000', '1000000').
          // The backend may return numeric values after a concept save/load cycle.
          // Strict equality (===) in vue-slider means 50000 !== '50000', so the slider
          // cannot find the saved option and resets to the first entry.
          // Converting to string here ensures the v-model value always matches the option keys.
          if (step.type === 'calculation' && step.data?.parameters) {
            const params = step.data.parameters;
            if (params.target?.amount != null) {
              params.target.amount = String(params.target.amount);
            }
            if (params.saving?.initial_deposit != null) {
              params.saving.initial_deposit = String(params.saving.initial_deposit);
            }
            if (params.saving?.regular_deposit != null) {
              params.saving.regular_deposit = String(params.saving.regular_deposit);
            }
          }
        }

        const lastStepLS = localStorage.getItem('ONBOARDING_LAST_STEP');
        let usedLastStep = 0;
        if (lastStepLS !== null) {
          usedLastStep = parseInt(lastStepLS, 10);
          localStorage.removeItem('ONBOARDING_LAST_STEP');
        } else {
          if (props.assistedOnboarding && loggedUser.value?.client) {
            usedLastStep = form.steps.findIndex(st => st.id === 'summary');
          } else {
            usedLastStep = form.current_step_index === 0 ? 0 : form.current_step_index + 1;
          }
        }

        defaultOnboardingSettings.value = form.default_onboarding_settings;
        defaultOnboardingSettingsByCurrency.value = form.default_onboarding_settings_by_currency ?? {};

        /*if (usedLastStep !== 0 && loggedUser.value?.client && form.value?.is_assisted_onboarding) {

        }*/

        stepIndex.value = usedLastStep;

        steps.value = [...steps.value, ...form.steps];

        interactionFormsID.value = form.id;
        interactionFormState.value = form.state;
        // IMA-486: načíst přílohy hned po zjištění ID formuláře, ne až při vstupu do summary
        // kroku přes watch() - po uložení konceptu a znovuotevření nemusí uživatel na summary
        // krok vůbec dojít hned, a přílohy by se tak nikdy nenačetly.
        loadInteractionFormAttachments();
        step.value = steps.value[usedLastStep];
        isAssistedOnboarding.value = form.is_assisted_onboarding ?? false;
        additionalDocumentsEnabled.value = form.additional_documents_enabled ?? false;
        interactionFormClientId.value = form.client_id ?? null;
        // Načíst rovnou (ne až po zatržení checkboxu) - potřebujeme vědět, jestli vůbec
        // nějaké nepodepsáné formuláře existují, abychom volbu jinak rovnou skryli.
        if (additionalDocumentsEnabled.value) {
          loadAvailableWaitingForms();
        }
        // Restore checkbox state when reopening a concept that already had forms attached.
        const summaryStepData = form.steps.find(s => s.type === 'summary')?.data;
        if (summaryStepData?.additional_form_ids?.length > 0) {
          showAddForms.value = true;
        }

        if (isAssistedOnboarding.value) {
          const meetingRecordStep = steps.value.find(step => step.type === 'meeting_records');
          introMeetingRecordsSummary.value = meetingRecordStep?.meeting_record_summary ?? null;
          if (loggedUser?.value.client) {
            showAssistedOnboardingIntro.value = true;
          }
        }

        if (!allowedBankID()) {
          selectDocumentMethod();
        }

        // On concept reload: detect whether the advisor manually changed the meeting_record
        // text by comparing the last generated_summary_text (stored in the calculation step)
        // with the current meeting_content value. If they differ → manually edited.
        const calcStepData = form.steps.find(s => s.type === 'calculation');
        const mrStepData = form.steps.find(s => s.type === 'meeting_records');
        const storedGenerated = calcStepData?.data?.generated_summary_text ?? null;
        const currentMrText = getMeetingRecordTextFromStep(mrStepData);
        if (storedGenerated !== null && currentMrText !== null && storedGenerated !== currentMrText) {
          meetingRecordModified.value = true;
          lastGeneratedSummaryText.value = storedGenerated;
        }

        // Restore identification state when opening a concept that was saved on that step
        if (step.value.type === 'identification') {
          restoreIdentificationState();
        }

        // send bankID code to BE
        const bankIdCode = localStorage.getItem('BANKID_IDENTIFICATION_CODE');
        if (bankIdCode !== null && bankIdCode !== 'null') {
          step.value.data = {bank_id_code: bankIdCode};
          processIdentificationWithBankID();
        }

        getAvailableInvestments();
      });
    }
  });
}

function getInteractionForm() {
  if (props.assistedOnboarding) {
    return Promise.resolve(props.assistedOnboarding);
  } else {
    return userService.getInteractionForms();
  }
}

function startAgain() {
  stepIndex.value = 0;
  step.value = steps.value[0];
  scrollToTop();
}

function nextStep() {
  if (step.value?.type === 'meeting_records') {
    checkMeetingRecordModified();
  }

  if (showAssistedOnboardingIntro.value) {
    showAssistedOnboardingIntro.value = false;
  } else {
    stepIndex.value++;
  }

  step.value = steps.value[stepIndex.value];

  scrollToTop();

  if (step.value.type === 'address') {
    if (!step.value.data.correspondence_address || step.value.data.correspondence_address?.length === 0) {
      step.value.data.correspondence_address = {};
    }
    if (!step.value.data.domicile_address || step.value.data.domicile_address?.length === 0) {
      step.value.data.domicile_address = {};
    }
    if (step.value.data.correspondence_address && step.value.data.correspondence_address.length) {
      step.value.data.correspondence_not_the_same = true;
    }
  }

  if (step.value.type === 'bank_account' && !step.value.data.currencies) {
    step.value.data.currencies = [];
  }
  // Pre-select declaration_owner for new bank accounts (no existing account loaded means declaration_owner is unset)
  if (step.value.type === 'bank_account' && step.value.data.declaration_owner === undefined) {
    step.value.data.declaration_owner = true;
  }

  if (step.value.type === 'calculation') {
    getAvailableInvestments();
  }

  if (step.value.type === 'identification') {
    restoreIdentificationState();
  }

  if (step.value.type === 'confirmation') {
    generateCode();
  }
}

function scrollToTop() {
  // scroll to top of the overlay
  const popup = document.getElementById('app-popup-scrollable-element');
  if (popup) {
    popup.scrollTo({top: 0, behavior: 'smooth'});
  }
}

function processIdentification() {
  step.value.data.method = selectedIdentificationMethod.value;
  if (selectedIdentificationMethod.value === 'DOCUMENT') {
    step.value.data.identification_documents = [0, 1].map(i => {
      const docType = selectedIdentificationDocumentTypes.value[i];
      return {
        document_type: docType,
        // Pass existing file IDs so the backend keeps them (minus any the user removed).
        files_ids: step.value.data?.attachments?.[docType] ?? [],
        attachments: addedFiles.value.filter(f => f.dropzone_index === i)
      };
    });
  }

  processInteractionFormsStep();
}

function processIdentificationWithBankID() {
  localStorage.removeItem('BANKID_IDENTIFICATION_CODE');
  selectedIdentificationMethod.value = 'BANK_ID';
  step.value.data.method = 'BANK_ID';
  setButtonContentLoading('btn-bankid-login');
  bankIDIdentificationProcessing.value = true;
  userService.processInteractionFormsStep(step.value, interactionFormsID.value)
      .then((data) => {
        refreshSteps(data.steps);
        bankIDIdentificationDone.value = true;
      })
      .catch(() => {
        // Zpracování BankID kódu na backendu může selhat (expirovaný kód, výpadek BankID apod.).
        // Bez tohoto catch zůstal spinner "Probíhá identifikace..." viset navždy (IMA-502).
        bankIDIdentificationProcessing.value = false;
        showErrorMessage(t('clientOnboardingOverlay.identification_bank_id_failed'));
      })
      .finally(() => {
        setButtonContentDefault('btn-bankid-login');
      });
}

function selectDocumentMethod() {
  selectedIdentificationMethod.value = 'DOCUMENT';
  selectedIdentificationDocumentTypes.value[0] = 'ID_CARD';
  selectedIdentificationDocumentTypes.value[1] = 'DRIVER_LICENSE';

  getAvailableDocumentTypes();
}

// Restore identification UI state (method + document types) from previously saved concept data.
// Called whenever the identification step becomes active so that previously uploaded files
// and the selected document types are shown correctly on concept reload.
function restoreIdentificationState() {
  const savedMethod = step.value?.data?.method;
  if (!savedMethod) return;

  selectedIdentificationMethod.value = savedMethod;

  if (savedMethod === 'DOCUMENT') {
    const attachments = step.value?.data?.attachments;
    const metadata = step.value?.data?.attachments_metadata;

    if (attachments && typeof attachments === 'object') {
      const docTypes = Object.keys(attachments);
      if (docTypes.length > 0) selectedIdentificationDocumentTypes.value[0] = docTypes[0];
      if (docTypes.length > 1) selectedIdentificationDocumentTypes.value[1] = docTypes[1];
    }

    getAvailableDocumentTypes();

    // Inject previously uploaded files into addedFiles so they appear in the same
    // per-dropzone list as newly added files — with the same Remove button — and so
    // hiddenDropzone hides the slot correctly once a file exists.
    // Guard: skip if pseudo-objects were already injected (e.g. user navigated away and back).
    const alreadyInjected = addedFiles.value.some(f => f.isPreviouslyUploaded);
    if (!alreadyInjected && metadata && typeof metadata === 'object') {
      const pseudoFiles = [];
      for (const [dropzoneIdx, docType] of Object.entries(selectedIdentificationDocumentTypes.value)) {
        const idx = Number(dropzoneIdx);
        const files = metadata[docType];
        if (!Array.isArray(files)) continue;
        files.forEach((file, filePos) => {
          let entity;
          if (docType === 'ID_CARD') {
            entity = filePos === 0 ? 'front_page' : 'back_page';
          } else {
            entity = docType + '_' + (filePos + 1);
          }
          pseudoFiles.push({
            name: file.name,
            entity,
            dropzone_index: idx,
            upload: { uuid: 'existing_' + file.id },
            // Marker used by removeFile() to also clean step.data.attachments
            isPreviouslyUploaded: true,
            existingFileId: file.id,
            existingDocType: docType,
          });
        });
      }
      if (pseudoFiles.length > 0) {
        addedFiles.value = [...pseudoFiles, ...addedFiles.value];
      }
    }
  }
}

// alsoRefreshIds — volitelný seznam ID kroků, které se mají aktualizovat z API odpovědi
// i tehdy, jsou-li ve stavu COMPLETED. Slouží pro případy, kdy backend změní data
// dříve uloženého kroku (např. kalkulačka přepisuje pole v záznamu z jednání).
function refreshSteps(newSteps, getNextStep = true, alsoRefreshIds = []) {
  const index = steps.value.findIndex(st => st.id === step.value.id);
  const summaryIndex = steps.value.findIndex(st => st.id === 'summary');

  steps.value[index].state = 'COMPLETED';
  if (getNextStep) {
    steps.value.forEach((localStep, i) => {
      const forceRefresh = alsoRefreshIds.includes(localStep?.id);
      if ((localStep?.state === 'WAITING' || forceRefresh) && newSteps[i - 1]) {
        if (!newSteps[i - 1].data) {
          newSteps[i - 1].data = {};
        }
        steps.value[i] = newSteps[i - 1];
      }
    });
  } else {
    steps.value[index] = newSteps[index - 1];
    steps.value[summaryIndex] = newSteps[summaryIndex - 1];
  }
}

/**
 * Odešle aktuální krok na BE.
 *
 * Chybu zpracuje sama (errorMessage), ale vrací Promise s výsledkem, aby volající, který
 * potřebuje vědět, jestli se uložení povedlo (saveAsConcept), nemusel hádat.
 *
 * @param {boolean} nextStepAllowed po úspěchu přejít na další krok
 * @returns {Promise<boolean>} true = krok uložen, false = BE vrátil chybu
 */
function processInteractionFormsStep(nextStepAllowed = true) {
  const iWantBackToSummary = goBackToSummaryAfterStepSubmit.value;
  if (goBackToSummaryAfterStepSubmit.value) {
    nextStepAllowed = false;
  }
  setButtonContentLoading('step-submit-button');
  return userService.processInteractionFormsStep(step.value, interactionFormsID.value)
      .then((data) => {
        //refreshSteps(data.steps);
        // Při ukládání kalkulačky backend přepíše pole záznamu z jednání (Jednorázová/Pravidelná
        // investice, Doba investování) — tyto kroky jsou COMPLETED, refreshSteps() je normálně
        // přeskočí. Proto je explicitně předáváme v alsoRefreshIds.
        const alsoRefreshIds = step.value.id === 'calculation' ? ['meeting_records'] : [];
        if (nextStepAllowed) {
          refreshSteps(data.steps, true, alsoRefreshIds);
          nextStep();
        } else {
          refreshSteps(data.steps, false);
          const summaryStep = steps.value.find(st => st.id === 'summary');
          if (summaryStep && summaryStep.data.payment_informations) {
            paymentInformations.value = summaryStep.data.payment_informations;
          }
          iWantBackToSummary ? showAnotherInformation.value = false : showAnotherInformation.value = true;
          if (goBackToSummaryAfterStepSubmit.value) {
            goBackToSummaryAfterStepSubmit.value = false;
            goToStepByID('summary');
          }
        }
        return true;
      })
      .catch(error => {
        // Dřív se tu vždy ukázalo "Neplatný autorizační kód" bez ohledu na skutečnou chybu
        // (např. 403 při read-only účtu) - teď zobrazíme reálnou zprávu z backendu a jen
        // pro skutečně neplatný kód necháváme přátelštější přeložený text (viz SignModal.vue).
        errorMessage.value = error?.message ?? 'Něco se pokazilo';
        if (error?.message === 'verification_code.INVALID_CODE') {
          errorMessage.value = t('login.invalid_auth_code');
        }
        if (error?.message === 'verification_code.TOO_MANY_ATTEMPTS') {
          errorMessage.value = t('verification_code.TOO_MANY_ATTEMPTS');
        }
        return false;
      })
      .finally(() => {
        setButtonContentDefault('step-submit-button');
      });
}

function skipStep() {
  step.value.skip_step = true;
  processInteractionFormsStep();
}

function processCalculationStep() {
  // Regenerate generated_summary_text only if the advisor has not manually changed the
  // meeting_records text. meeting_records precedes calculation in the step order and is
  // always COMPLETED by the time the calculator runs, so we cannot use its state as a
  // guard — we rely solely on meetingRecordModified (set by text comparison).
  if (!meetingRecordModified.value) {
    const newText = generateCalcSummaryText();
    lastGeneratedSummaryText.value = newText;
    step.value.data.generated_summary_text = newText;
    // Keep local meeting_records data in sync so checkMeetingRecordModified()
    // does not see a stale value and falsely flag the text as manually edited.
    setMeetingRecordText(newText);
  }
  processInteractionFormsStep();
}

function processConfirmation() {
  processInteractionFormsStep(false);
}

function processAggregatedFormStep() {
  if (aggregatedFormStepIndex.value === aggregatedFormsCount.value - 1) {
    processInteractionFormsStep();
    return;
  }
  aggregatedFormStepIndex.value++;
  scrollToTop();
}

// Read the meeting_content question value from a given step object (or the live step from steps[]).
function getMeetingRecordTextFromStep(mrStep) {
  for (const fieldset of mrStep?.data?.content ?? []) {
    for (const q of fieldset?.questions ?? []) {
      if (q.id === 'meeting_content') return q.value ?? '';
    }
  }
  return null;
}

function getMeetingRecordText() {
  return getMeetingRecordTextFromStep(steps.value.find(s => s.type === 'meeting_records'));
}

// Write text into the local meeting_content question so that lastGeneratedSummaryText
// and the local steps[] stay in sync. refreshSteps() skips COMPLETED steps, so without
// this the comparison in checkMeetingRecordModified() would always see a stale value
// and incorrectly flag the text as manually edited.
function setMeetingRecordText(text) {
  const mrStep = steps.value.find(s => s.type === 'meeting_records');
  for (const fieldset of mrStep?.data?.content ?? []) {
    for (const q of fieldset?.questions ?? []) {
      if (q.id === 'meeting_content') {
        q.value = text;
        return;
      }
    }
  }
}

// Compare the current meeting_content value with what the calculator last generated.
// Sets meetingRecordModified if they actually differ (= advisor changed the text).
function checkMeetingRecordModified() {
  if (lastGeneratedSummaryText.value === null) return;
  const current = getMeetingRecordText();
  if (current !== null && current !== lastGeneratedSummaryText.value) {
    meetingRecordModified.value = true;
  }
}

function processMeetingRecord() {
  checkMeetingRecordModified();
  processInteractionFormsStep();
}

function processStrategies() {
  const selectedStrategies = step.value.available_values.filter(strategy => strategy.checked === true);
  step.value.data = selectedStrategies.map(strategy => strategy.id);
  processInteractionFormsStep();
}

function authenticateWithBankID() {
  checkForBankIdLogin();
  setButtonContentLoading('btn-bankid-login');
  getBankIdUrl('')
      .then(response => {
        localStorage.setItem('BANKID_IDENTIFICATION_CODE', null);
        localStorage.setItem('ONBOARDING_LAST_STEP', stepIndex.value);
        if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('Browser')) {
          void Browser.open({url: response.url});
        } else {
          window.location.href = response.url;
        }
      }, error => {
        setButtonContentDefault('btn-bankid-login');
        showErrorMessage(error.response?.data?.message);
      });
}

/**
 * Vrátí jen soubory, které Dropzone přijala.
 *
 * Event 'addedfiles' přichází až po synchronní kontrole Dropzone (velikost, typ, počet),
 * ale obsahuje i odmítnuté soubory (file.accepted === false). Dřív se posílaly na BE
 * všechny - příliš velký doklad tak prošel až k uložení a spadl tam (Sentry
 * CLIENT-APP-BACKEND-4Y). Hlášku o odmítnutí zobrazí CustomDropzone (onUploadError).
 *
 * @param {FileList|File[]} files soubory z eventu 'addedfiles'
 * @returns {File[]} přijaté soubory
 */
function acceptedDropzoneFiles(files) {
  return Array.from(files).filter(file => file.accepted !== false);
}

function removeFile(file) {
  addedFiles.value = addedFiles.value.filter(f => f.upload.uuid !== file.upload.uuid);
  // If this is a previously uploaded file (injected as a pseudo-object on concept reload),
  // also remove its ID from step.data.attachments so it is excluded on the next save.
  if (file.isPreviouslyUploaded && file.existingDocType) {
    if (step.value?.data?.attachments?.[file.existingDocType]) {
      step.value.data.attachments[file.existingDocType] = step.value.data.attachments[file.existingDocType].filter(id => id !== file.existingFileId);
    }
    if (step.value?.data?.attachments_metadata?.[file.existingDocType]) {
      step.value.data.attachments_metadata[file.existingDocType] = step.value.data.attachments_metadata[file.existingDocType].filter(f => f.id !== file.existingFileId);
    }
  }
}

function endOnboarding() {
  emitter.emit('onboardingDone');
}

function showBankAccountFn() {
  return step.value.data.is_multicurrency
      ? step.value.data.currencies && step.value.data.currencies.length === 1 && step.value.data.currencies.includes('CZK')
      : step.value.data.currencies?.[0] === 'CZK';
}

const generateCode = () => {
  codeSentLoading.value = true;
  codeSentAgain.value = true;
  errorMessage.value = null;
  const form = {
    verifiable_type: 'INTERACTION_FORM',
    verifiable_id: interactionFormsID.value,
    service: 'SMS',
    recipient: step.value.data.phone,
  };
  setButtonContentLoading('btn-activate');

  generate2faCode(form, 'api/verification-code/generate').then(response => {
    codeRequestInterval.value = 30;
    codeSent.value = true;
    setButtonContentDefault('btn-activate');
    startStopwatch();
    codeSentLoading.value = false;
  }, (error) => {
    errorMessage.value = error.message;
    setButtonContentDefault('btn-activate');
    codeSentAgain.value = false;
    codeSentLoading.value = false;
  });
};

const startStopwatch = () => {
  const interval = setInterval(() => {
    codeRequestInterval.value--;
    if (codeRequestInterval.value <= 0) {
      clearInterval(interval);
      codeSentAgain.value = false;
    }
  }, 1000);
};

function setDeclarations(property) {
  if (property === 'declaration_owner') {
    step.value.data.declaration_spouses_property = false;
  } else if (property === 'declaration_spouses_property') {
    step.value.data.declaration_owner = false;
  }
}

const verificationForm = ref(null);

function handleVerificationShow() {
  if (step.value.data.terms_agree && !codeSent.value) {
    showVerification.value = true;
    nextTick(() => {
      verificationForm.value?.scrollIntoView({
        block: "start",
        behavior: "smooth"
      });
      window.scrollBy({
        top: 40,
        behavior: "smooth"
      });
    });
    generateCode();
  }
}

function getAvailableInvestments() {
  // find step which has id "calculation"; fallback to current index if not found
  let calcStep = steps.value.find(st => st?.id === 'calculation');
  availableStrategies.value = calcStep?.available_values;
  const list = Array.isArray(availableStrategies.value) ? availableStrategies.value : [];
  if (list.length > 0) {
    bestPerformanceStrategy.value = list.reduce((best, current) => {
      const bestPerf = typeof best?.performance === 'number' ? best.performance : (parseFloat(best?.performance) || 0);
      const currPerf = typeof current?.performance === 'number' ? current.performance : (parseFloat(current?.performance) || 0);
      return currPerf > bestPerf ? current : best;
    }, list[0]);
    // Restore saved strategy from concept if available; fall back to best performance
    const savedStrategyId = calcStep?.data?.strategy_id;
    const savedStrategy = savedStrategyId ? list.find(s => s.id === savedStrategyId) : null;
    selectedStrategy.value = savedStrategy || bestPerformanceStrategy.value;
    setStrategy(selectedStrategy.value);
    recountCalculation();

  } else {
    bestPerformanceStrategy.value = null;
  }
}

function setStrategy(strategy) {
  // performance is always in this format: 8pct perf = 0.08
  performance.value = strategy?.performance ?? 0;

  // Ensure the calculation step's data reflects the chosen strategy
  const calcStep = steps.value.find(st => st?.id === 'calculation') || steps.value.find(st => st?.type === 'calculation');
  if (calcStep) {
    if (!calcStep.data) calcStep.data = {};
    calcStep.data.strategy_id = strategy?.id ?? null;
    // Optionally keep some readable fields for UI/back-end if expected
    if (strategy) {
      calcStep.data.strategy_name = strategy.strategy_name ?? strategy.name ?? calcStep.data.strategy_name;
      calcStep.data.performance = strategy.performance ?? calcStep.data.performance;
    }
  }
}

function previousStep() {
  if (step.value?.type === 'meeting_records') {
    checkMeetingRecordModified();
  }

  stepIndex.value--;
  step.value = steps.value[stepIndex.value];
  scrollToTop();

  if (step.value.type === 'identification') {
    restoreIdentificationState();
  }
}

function previousAggregatedFormStep() {
  aggregatedFormStepIndex.value--;
  scrollToTop();
}

// IMA-486: přílohy nahrané k tomuto konkrétnímu onboardingu (documentable_type = InteractionForm)
function loadInteractionFormAttachments() {
  if (!interactionFormsID.value) {
    return;
  }
  interactionFormService.getDocuments(interactionFormsID.value).then((response) => {
    interactionFormAttachments.value = response;
    syncAttachmentsIntoDropzone();
  });
}

function onAttachmentUploaded() {
  loadInteractionFormAttachments();
}

// true, jen když removeAllFiles()/manuallyAddFile() volané odsud samo o sobě spouští
// událost "removed" u dropzone - nesmí to spustit skutečné mazání na backendu (viz níže)
let isSyncingAttachmentsIntoDropzone = false;

// Zobrazí už nahrané přílohy přímo v dropzone (ne v samostatném seznamu) - dropzone-vue3
// na to má manuallyAddFile(), který vytvoří stejný náhled jako u reálně nahraného souboru.
async function syncAttachmentsIntoDropzone() {
  if (interactionFormAttachments.value.length > 0) {
    showAddAttachment.value = true;
  }
  await nextTick();
  const dropzone = attachmentsDropzoneRef.value;
  if (!dropzone) {
    // dropzone ještě není v DOM (např. nahráváme dřív, než uživatel dojde na krok summary) -
    // watch() na step.value?.type níže zavolá loadInteractionFormAttachments() znovu, jakmile tam bude
    return;
  }
  isSyncingAttachmentsIntoDropzone = true;
  try {
    dropzone.removeAllFiles(true);
    for (const attachment of interactionFormAttachments.value) {
      // manuallyAddFile() z dropzone-vue3 dělá fileUrl.indexOf(...) bez ochrany proti null -
      // prázdný string je jediná bezpečná "nemám URL" hodnota, jinak by spadla celá synchronizace
      // (a dropzone by zůstala vyprázdněná, pokud by u dokumentu chyběl download_url)
      dropzone.manuallyAddFile(
          {id: attachment.id, name: attachment.name, size: attachment.file?.size ?? 0},
          attachment.file?.download_url ?? '',
      );
    }
  } finally {
    isSyncingAttachmentsIntoDropzone = false;
  }
}

// Volá se z CustomDropzone i pro reálně rozpracovaný (ještě nenahraný) soubor smazaný
// uživatelem před dokončením uploadu - ten na backendu ještě neexistuje (file.id chybí).
function onAttachmentRemovedFromDropzone(file) {
  if (isSyncingAttachmentsIntoDropzone || !file?.id) {
    return;
  }
  DocumentService.removeDocument(file.id).then(() => {
    interactionFormAttachments.value = interactionFormAttachments.value.filter((document) => document.id !== file.id);
    showSuccessMessage(t('clientOnboardingOverlay.attachment_removed'));
  });
}

// krok shrnutí se v tomto souboru nemountuje samostatně (jeden velký v-if wizard),
// takže přílohy dotáhneme, jakmile do kroku summary vstoupíme
watch(() => step.value?.type, (type) => {
  if (type === 'summary') {
    loadInteractionFormAttachments();
  }
});

function openDocumentDetailModal(documentId) {
  event.preventDefault();
  const {reveal, onConfirm, onCancel} = createConfirmDialog(DocumentDetailModal, {documentId, isNewOnboarding: true});
  reveal();
  onConfirm(() => {
    getDocuments();
  }),
      onCancel(() => {
        getDocuments();
      });
}

function goToStepByID(id, goBackToSummary = false) {
  goBackToSummaryAfterStepSubmit.value = goBackToSummary;
  if (id === 'start') {
    stepIndex.value = 0;
    step.value = steps.value[stepIndex.value];
  } else {
    const stepToRedirect = steps.value.find(step => step.id === id);
    stepIndex.value = steps.value.indexOf(stepToRedirect);
    step.value = steps.value[stepIndex.value];
  }
  scrollToTop();
}

function saveAsConcept() {
  if (step.value.type !== 'summary') {
    step.value.save_as_concept = true;

    // Bug fix: uploaded identity documents exist only in memory (addedFiles ref).
    // They are normally sent to the backend by processIdentification(), which assembles
    // identification_documents before calling processInteractionFormsStep().
    // When saving as a concept the user skips processIdentification(), so we must
    // build the same payload here — otherwise the backend receives no files and the
    // documents disappear when the advisor reopens the concept.
    if (step.value.type === 'identification' && selectedIdentificationMethod.value === 'DOCUMENT') {
      step.value.data.method = selectedIdentificationMethod.value;
      step.value.data.identification_documents = [0, 1].map(i => {
        const docType = selectedIdentificationDocumentTypes.value[i];
        return {
          document_type: docType,
          // Pass existing file IDs so the backend keeps them (minus any the user removed).
          files_ids: step.value.data?.attachments?.[docType] ?? [],
          attachments: addedFiles.value.filter(f => f.dropzone_index === i)
        };
      });
    }

    // Onboarding zavřít a ohlásit uložení až po odpovědi BE - dřív se zavřel hned a ukázal
    // "uloženo", i když BE uložení odmítl (např. příliš velký doklad), takže klient/poradce
    // odešel v domnění, že doklady jsou nahrané. Při chybě zůstává onboarding otevřený
    // s errorMessage a uživatel může soubor vyměnit.
    processInteractionFormsStep().then(saved => {
      if (!saved) {
        step.value.save_as_concept = false;
        showErrorMessage(errorMessage.value);
        return;
      }
      endOnboarding();
      showSuccessMessage(t('clientOnboardingOverlay.onboarding_saved_as_concept'));
    });
    return;
  }
  endOnboarding();
  showSuccessMessage(t('clientOnboardingOverlay.onboarding_saved_as_concept'));
}

function changeState(state) {
  // IMA-500: "Odeslat na klienta" jde úplně jinou cestou než processInteractionFormsStep()
  // (SMS ověření) - bez tohohle by se výběr formulářů k přiložení na kroku summary ztratil.
  interactionFormService.changeState(interactionFormsID.value, state, step.value?.data?.additional_form_ids);
  endOnboarding();
}

const uncompletedSteps = computed(() =>
    steps.value.filter(step =>
        step.state !== 'COMPLETED' && !['start', 'end', 'summary'].includes(step.type)
    )
);

const generateCalcSummaryText = () => {
  let str = '';
  if (isAssistedOnboarding.value) {
    str += t('clientOnboardingOverlay.calculation_summary_text_1_aso');
  } else {
    str += t('clientOnboardingOverlay.calculation_summary_text_1');
  }

  if (step.value.data.mode === 'TARGET') {
    str += t('clientOnboardingOverlay.calculation_summary_text_2') + formatAmount(step.value.data.parameters.target.amount, step.value.data.parameters.general.currency, 0) + ', ' + t('clientOnboardingOverlay.calculation_summary_text_3') + step.value.data.parameters?.general.duration + t('clientOnboardingOverlay.calculation_summary_text_4');
  }
  if (step.value.data.mode === 'SAVING') {
    str += t('clientOnboardingOverlay.calculation_summary_text_5') + step.value.data.parameters.general.duration + t('clientOnboardingOverlay.calculation_summary_text_6') + formatAmount(step.value.data.parameters.saving.initial_deposit, step.value.data.parameters?.general.currency, 0) + ', ' + t('clientOnboardingOverlay.calculation_summary_text_7') + formatAmount(step.value.data.parameters.saving.regular_deposit, step.value.data.parameters.general.currency, 0) + '.';
  }
  if (selectedStrategy.value?.name) {
    str += t('clientOnboardingOverlay.calculation_summary_text_8') + selectedStrategy.value?.name + '.';
  }

  return str;
};

const getAvailableDocumentTypes = (dropzoneIndex) => {
  // reset current dropzone
  addedFiles.value = addedFiles.value.filter(f => f.dropzone_index !== dropzoneIndex);

  for (const index of [0, 1]) {
    const available = [];
    for (const type of allAvailableDocumentTypes) {
      if (type.value !== selectedIdentificationDocumentTypes.value[0] && type.value !== selectedIdentificationDocumentTypes.value[1]) {
        available.push(type);
      }
      if (selectedIdentificationDocumentTypes.value[index] === type.value) {
        available.push(type);
      }
    }
    availableDocumentTypes.value[index] = [...available];
  }
};

const isIdentificationButtonEnabled = computed(() => {
  return checkUploadedFilesByDocumentType(0) && checkUploadedFilesByDocumentType(1)
});

const checkUploadedFilesByDocumentType = (index) => {
  const filesLength = addedFiles.value.filter(f => f.dropzone_index === index)?.length;
  if (selectedIdentificationDocumentTypes.value[index] === 'ID_CARD') {
    return filesLength === 2
  } else {
    if (isAssistedOnboarding.value && index !== 0) {
      return filesLength >= 0
    } else {
      return filesLength >= 1
    }
  }
}

function setCurrency() {
  const selectedCurrency = step.value.data.parameters.general.currency;
  currency.value = selectedCurrency;

  // Rebuild deposit limits for the newly selected currency so CurrencyInput min/max props
  // update reactively via currentCurrencySettings. Also clamp current model values so the
  // chart recalculates correctly even before the user touches the inputs again.
  // defaultOnboardingSettingsByCurrency is populated from form.default_onboarding_settings_by_currency
  // (backend returns settings for all available currencies, not just the tenant default).
  const currencySettings = defaultOnboardingSettingsByCurrency.value[selectedCurrency];
  if (currencySettings) {
    buildSliderOptions(currencySettings);

    // Clamp current deposit values to the new currency's limits — a value valid in CZK
    // may exceed the EUR max (e.g. 5 000 000 CZK > 200 000 EUR max)
    const params = step.value.data.parameters;
    params.saving.initial_deposit = Math.min(
      Math.max(Number(params.saving.initial_deposit), currencySettings.initial_deposit_min),
      currencySettings.initial_deposit_max
    );
    params.saving.regular_deposit = Math.min(
      Math.max(Number(params.saving.regular_deposit), currencySettings.regular_deposit_min),
      currencySettings.regular_deposit_max
    );
    calculationUpdated();
  }
}

function openInvestmentQuestionnaireResultsModal(step) {
  if (step.investment_questionnaire_results) {
    const {reveal, onConfirm, onCancel} = createConfirmDialog(InvestmentQuestionnaireResultsModal, {results: step.investment_questionnaire_results});
    reveal();
    onConfirm((data) => {
    });
    onCancel(() => {
    });
  }
}

// Na rozdíl od Kalkulačky (kde jsou investment_questionnaire_results předpočítané už
// z předchozího kroku aggregated_form) se AML dotazník vyplňuje přímo v tomto kroku -
// vyhodnocení se proto musí dopočítat na vyžádání z rozpracovaných odpovědí (bez
// posunutí kroku dál), obdoba tlačítka "Vyhodnotit" ve standalone AmlQuestionnaireModal.
// Posílá se step.value.data (ne přefiltrovaná amlVisibleStepData) - stejná data, jaká by
// šla i při skutečném odeslání kroku, včetně otázek skrytých klientovi (visible_to_client: false).
// Vyhodnocení trvá cca 2s - tlačítko proto místo textu zobrazí spinner + "Načítám" (viz
// amlResultLoading v šabloně), aby uživatel neklikal opakovaně.
const amlResultLoading = ref(false);
function openAmlQuestionnaireResultsModal() {
  amlResultLoading.value = true;
  interactionFormService.evaluateAmlStepPreview(interactionFormsID.value, step.value.data)
    .then(response => {
      const {reveal, onConfirm, onCancel} = createConfirmDialog(AmlQuestionnaireResultsModal, {results: response.result});
      reveal();
      onConfirm(() => {
      });
      onCancel(() => {
      });
    })
    .catch(() => {
      showErrorMessage();
    })
    .finally(() => {
      amlResultLoading.value = false;
    });
}
</script>

<template>
  <div class="app-popup">
    <OnboardingHeader></OnboardingHeader>
    <div class="circles-container-wrapper">
      <div class="circles-container" style="top: 310px; right: -220px;">
        <div class="circles__img"></div>
      </div>
      <div class="circles-container" style="left: -130px; top: 20px;">
        <div class="circles__img"></div>
      </div>
    </div>
    <div id="app-popup-scrollable-element" class="row h-100 overflow-auto justify-content-center px-0 py-5 px-lg-5 mx-0" style="z-index: 1055">
      <div class="col-md-8 mb-5">
        <div v-if="!showAnotherInformation" class="app-popup__title">
          <span v-if="showAssistedOnboardingIntro">{{ t('clientOnboardingOverlay.requirements_to_processed_new') }}</span>
          <span v-else>{{ step?.title }}</span>
        </div>
        <div class="app-popup__content">
          <div :class="step.type !== 'start' && !showAssistedOnboardingIntro? 'card' : ''">
            <div v-if="step.type !== 'start' && !showAnotherInformation && !showAssistedOnboardingIntro" class="card-header border-bottom p-3">
              <div class="row">
                <div class="col-12 mb-2 d-flex justify-content-between">
                  <small>{{ t('clientOnboardingOverlay.step') }} {{ stepIndex + 1 }} / {{ steps.length }}</small>
                  <small>{{ step?.title }}</small>
                </div>
              </div>
              <div class="progress">
                <div class="progress-bar" role="progressbar" :style="{width: (((stepIndex + 1) / steps.length) * 100) + '%'}"></div>
              </div>
            </div>
            <div class="card-body">
              <template v-if="step.type === 'start' && !showAssistedOnboardingIntro">
                <div class="row mb-5 justify-content-center">
                  <template v-for="step of steps">
                    <!-- AML dotazník se v úvodním přehledu kroků nezobrazuje (samotný krok
                         v onboardingu zůstává nezměněn) -->
                    <div v-if="step.type !== 'start' && step.type !== 'end' && step.type !== 'aml'" class="col-md-6">
                      <div class="alert alert-light">
                        <span v-if="step.icon" class="mdi-18px me-2 mdi" style="vertical-align: middle" :class="step.icon"></span>
                        <span style="vertical-align: middle">{{ step?.title }}</span>
                      </div>
                    </div>
                  </template>
                  <div class="col-12">
                    <div class="small">
                      {{ isBidli ? t('clientOnboardingOverlay.hello_requirements_to_processed_footer_text_new') : t('clientOnboardingOverlay.hello_requirements_to_processed_footer_text_2') }}
                    </div>
                  </div>
                </div>
                <div v-if="!loggedUser.client && assistedOnboarding" class="row">
                  <div class="col-12 d-flex justify-content-between">
                    <button class="btn btn-primary btn-md" type="button" @click="endOnboarding">{{ t('clientOnboardingOverlay.back_to_app') }}</button>
                    <button class="btn btn-primary btn-md" type="button" @click="nextStep">
                      <span>{{ t('clientOnboardingOverlay.start_onboarding') }}</span>
                      <span class="arrow-right-icon"></span>
                    </button>
                  </div>
                </div>
                <div v-else class="row">
                  <div class="col-12 text-end">
                    <button class="btn btn-primary btn-md" type="button" @click="nextStep">
                      <span>{{ t('clientOnboardingOverlay.start_onboarding') }}</span>
                      <span class="arrow-right-icon"></span>
                    </button>
                  </div>
                </div>
              </template>
              <template v-if="loggedUser?.client && interactionFormState === 'WAITING_FOR_CLIENT' && showAssistedOnboardingIntro">
                <div class="row mb-3">
                  <div class="col-12">
                    {{ t('clientOnboardingOverlay.start_intro') }}
                  </div>
                </div>
                <div class="row mb-5 justify-content-center">
                  <div class="alert alert-light">
                    <span class="mdi-18px me-2 mdi mdi-account-check" style="vertical-align: middle"></span>
                    <span style="vertical-align: middle">{{ t('clientOnboardingOverlay.start_1') }}</span>
                  </div>
                  <div class="alert alert-light">
                    <span class="mdi-18px me-2 mdi mdi-fountain-pen-tip" style="vertical-align: middle"></span>
                    <span style="vertical-align: middle">{{ t('clientOnboardingOverlay.start_2') }}</span>
                  </div>
                  <div class="alert alert-light">
                    <span class="mdi-18px me-2 mdi mdi-format-list-bulleted" style="vertical-align: middle"></span>
                    <span style="vertical-align: middle">{{ t('clientOnboardingOverlay.start_3') }}</span>
                    <span v-if="introMeetingRecordsSummary" class="fw-bold"><br/>{{ introMeetingRecordsSummary }}</span>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 text-end">
                    <button class="btn btn-primary btn-md" type="button" @click="nextStep()">
                      <span v-if="showAssistedOnboardingIntro">{{ t('clientOnboardingOverlay.go_to_signature') }}</span>
                      <span v-else>{{ t('clientOnboardingOverlay.start_onboarding') }}</span>
                      <span class="arrow-right-icon"></span>
                    </button>
                  </div>
                </div>
              </template>
              <template v-if="!showAssistedOnboardingIntro">
                <template v-if="step.type === 'aggregated_form'">
                  <div v-if="aggregatedFormStepIndex === 0" class="row mb-3">
                    <div class="col">
                      <p class="fw-bold fst-italic fs-5">
                        {{ t('clientOnboardingOverlay.fill_form') }}
                      </p>
                    </div>
                  </div>
                  <form @submit.prevent="processAggregatedFormStep" :key="step.id">
                    <div class="modal-body">
                      <DynamicForm :disabled="false" :config="step.data" :step-index="aggregatedFormStepIndex" ref="dynamicFormRef"></DynamicForm>
                    </div>

                    <div v-if="step" class="d-flex justify-content-between align-items-center">
                      <div style="min-width: 130px">
                        <button
                            type="button"
                            v-if="step && aggregatedFormStepIndex < aggregatedFormsCount && aggregatedFormStepIndex > 0"
                            class="btn btn-md"
                            @click="previousAggregatedFormStep"
                        >
                          <span class="arrow-left-icon"></span>
                          <span>{{ t('clientOnboardingOverlay.back') }}</span>
                        </button>
                      </div>
                      <div>{{ t('clientOnboardingOverlay.page') }} {{ aggregatedFormStepIndex + 1 }} / {{ aggregatedFormsCount }}</div>
                      <div>
                        <button v-if="aggregatedFormStepIndex !== aggregatedFormsCount - 1" class="btn btn-primary btn-md" type="submit">
                          <span>{{ t('clientOnboardingOverlay.next_page') }}</span>
                          <span class="arrow-right-icon"></span>
                        </button>
                        <button v-if="aggregatedFormStepIndex === aggregatedFormsCount - 1" class="btn btn-primary btn-md" id="step-submit-button" type="submit">
                          <span>{{ t('clientOnboardingOverlay.continue') }}</span>
                          <span class="arrow-right-icon"></span>
                        </button>
                      </div>
                    </div>
                  </form>
                </template>
                <template v-if="step.type === 'identification'">
                  <!-- IMA-519: upozornění na odstávku Bank iD přímo u volby metody identifikace.
                       Zobrazujeme i v ASO, kde je tlačítko pro zprostředkovatele skryté - klient
                       formulář následně dokončuje sám a Bank iD už nabídku má. -->
                  <AppAnnouncements :onlyBankId="true"/>
                  <div v-if="allowedBankID()" class="row mb-3">
                    <div class="col-12 mb-3 text-center">
                      {{ t('clientOnboardingOverlay.identification_intro') }}
                    </div>
                    <div class="col-12 d-flex justify-content-center align-items-center flex-column">
                      <button v-if="(selectedIdentificationMethod === null && !assistedOnboarding) || (selectedIdentificationMethod === 'BANK_ID' && !bankIDIdentificationDone)" :class="{'btn-content--loading': bankIDIdentificationProcessing === true}" id="btn-bankid-login" type="button" @click="authenticateWithBankID()" style="max-width: 300px" class="btn btn--with-icon btn--bank-id me-4">
                        <span class="btn-icon"></span>
                        <span class="btn-content ps-3">{{ t('clientOnboardingOverlay.identification_authenticate_bank_id') }}</span>
                      </button>
                      <div v-if="selectedIdentificationMethod === null && !assistedOnboarding" class="my-4"> {{ t('clientOnboardingOverlay.identification_or') }}</div>
                      <button v-if="selectedIdentificationMethod === null" class="btn btn-primary btn-md" type="button" @click="selectDocumentMethod"><span> {{ t('clientOnboardingOverlay.identification_upload_documents') }} </span></button>
                    </div>
                  </div>
                  <div>
                    <!--BANKID-->
                    <div v-if="selectedIdentificationMethod === 'BANK_ID'">
                      <div v-if="!bankIDIdentificationDone">
                        <div class="row mb-3">
                          <div v-if="bankIDIdentificationProcessing === true" class="col-12 text-center">
                            {{ t('clientOnboardingOverlay.identification_processing') }}
                          </div>
                        </div>
                      </div>
                      <div v-if="bankIDIdentificationDone">
                        <div class="row mb-3">
                          <div class="col-12 text-center">
                            <i class="mdi mdi-check icon-primary icon-big"></i>
                          </div>
                          <h3 class="col-12 mt-5 mb-5 text-center">
                            {{ t('clientOnboardingOverlay.identification_success') }}
                          </h3>
                        </div>
                      </div>
                    </div>
                    <!--DOCUMENTS-->
                  </div>

                  <div v-if="selectedIdentificationMethod === 'DOCUMENT'" class="modal-body">
                    <div class="row">
                      <div v-for="index in [0, 1]" :key="index" class="col-12 col-lg-6 mb-3">
                        <div class="">
                          <div class="row mb-3">
                            <div class="col-12">
                              <label for="typeInput" class="form-label">{{ t('clientOnboardingOverlay.identification_type') }} {{ index + 1 }}. {{ t('clientOnboardingOverlay.identification_of_document') }}</label>
                              <Select v-model="selectedIdentificationDocumentTypes[index]" @change="getAvailableDocumentTypes(index)" :options="availableDocumentTypes[index]" class="w-100"/>
                            </div>
                          </div>
                          <div class="row">
                            <div v-if="selectedIdentificationDocumentTypes[index] === 'ID_CARD'" class="col-12">
                              <div class="row">
                                <div class="col-12">
                                  <label class="form-label"> {{ t('clientOnboardingOverlay.identification_front_side') }} </label>
                                </div>
                              </div>
                              <div class="row">
                                <div class="col-12 form-group mb-3">
                                  <CustomDropzone v-if="!hiddenDropzone.front_page" :dropzone-options-input="{
                                  autoProcessQueue: false,
                                  url: getApiBaseUrl() + '/api/files/upload',
                                  acceptedFiles: '.jpg, .jpeg, .png, .pdf',
                                  maxFiles: 1,
                                  uploadMultiple: false,
                                  init() {
                                    this.on('addedfiles', function (files, xhr, formData) {
                                      const added = acceptedDropzoneFiles(files);
                                      added.forEach(file =>{
                                        file.entity = 'front_page';
                                        file.dropzone_index = index;
                                      })
                                      addedFiles = [...added, ...addedFiles];
                                    });
                                    },
                                }" :previews-disabled="true" :minimal="true"/>
                                </div>
                                <div v-if="addedFiles?.length > 0" class="col-12 mb-3">
                                  <div v-for="file of addedFiles" class="row">
                                    <template v-if="file?.entity === 'front_page'">
                                      <div class="col">
                                        <span>{{ file.name }}</span>
                                      </div>
                                      <div class="col-auto">
                                        <button type="button" class="btn btn-link btn-text-red pe-0" @click="removeFile(file)">{{ t('clientOnboardingOverlay.remove') }}</button>
                                      </div>
                                    </template>
                                  </div>
                                </div>
                              </div>

                              <div class="row">
                                <div class="col-12">
                                  <label class="form-label"> {{ t('clientOnboardingOverlay.identification_back_side') }} </label>
                                </div>
                              </div>
                              <div class="row">
                                <div class="col-12 form-group mb-3">
                                  <CustomDropzone v-if="!hiddenDropzone.back_page" :dropzone-options-input="{
                                  autoProcessQueue: false,
                                  url: getApiBaseUrl() + '/api/files/upload',
                                  acceptedFiles: '.jpg, .jpeg, .png, .pdf',
                                  maxFiles: 1,
                                  uploadMultiple: false,
                                  init() {
                                    this.on('addedfiles', function (files, xhr, formData) {
                                      const added = acceptedDropzoneFiles(files);
                                      added.forEach(file =>{
                                        file.entity = 'back_page';
                                        file.dropzone_index = index;
                                      })
                                      addedFiles = [...added, ...addedFiles];
                                    });
                                    },
                                }" :previews-disabled="true" :minimal="true"/>
                                </div>
                                <div v-if="addedFiles?.length > 0" class="col-12 mb-3">
                                  <div v-for="file of addedFiles" class="row">
                                    <template v-if="file?.entity === 'back_page'">
                                      <div class="col">
                                        <span>{{ file.name }}</span>
                                      </div>
                                      <div class="col-auto">
                                        <button type="button" class="btn btn-link btn-text-red pe-0" @click="removeFile(file)">{{ t('clientOnboardingOverlay.remove') }}</button>
                                      </div>
                                    </template>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div v-if="selectedIdentificationDocumentTypes[index] !== 'ID_CARD'" class="col-12">
                              <div class="row">
                                <div class="col-12">
                                  <label class="form-label"> {{ t('clientOnboardingOverlay.identification_document') }} </label>
                                </div>
                              </div>
                              <div class="row">
                                <div class="col-12 form-group mb-3">
                                  <CustomDropzone v-if="!hiddenDropzone?.[selectedIdentificationDocumentTypes[index] + '_1']" :dropzone-options-input="{
                                  autoProcessQueue: false,
                                  url: getApiBaseUrl() + '/api/files/upload',
                                  acceptedFiles: '.jpg, .jpeg, .png, .pdf',
                                  maxFiles: 20,
                                  uploadMultiple: true,
                                  init() {
                                    this.on('addedfiles', function (files, xhr, formData) {
                                      const added = acceptedDropzoneFiles(files);
                                      added.forEach(file =>{
                                        file.entity = selectedIdentificationDocumentTypes[index] + '_1';
                                        file.dropzone_index = index;
                                      })
                                      addedFiles = [...added, ...addedFiles];
                                    });
                                    },
                                }" :previews-disabled="true" :minimal="true"/>
                                </div>
                                <div v-if="addedFiles?.length > 0" class="col-12 mb-3">
                                  <div v-for="file of addedFiles" class="row">
                                    <template v-if="file?.entity === selectedIdentificationDocumentTypes[index] + '_1'">
                                      <div class="col">
                                        <span>{{ file.name }}</span>
                                      </div>
                                      <div class="col-auto">
                                        <button type="button" class="btn btn-link btn-text-red pe-0" @click="removeFile(file)">{{ t('clientOnboardingOverlay.remove') }}</button>
                                      </div>
                                    </template>
                                  </div>
                                </div>
                              </div>

                              <div class="row">
                                <div class="col-12">
                                  <label class="form-label"> {{ t('clientOnboardingOverlay.identification_next_file') }} </label>
                                </div>
                              </div>
                              <div class="row">
                                <div class="col-12 form-group mb-3">
                                  <CustomDropzone v-if="!hiddenDropzone?.[selectedIdentificationDocumentTypes[index] + '_2']" :dropzone-options-input="{
                                  autoProcessQueue: false,
                                  url: getApiBaseUrl() + '/api/files/upload',
                                  acceptedFiles: '.jpg, .jpeg, .png, .pdf',
                                  maxFiles: 20,
                                  uploadMultiple: true,
                                  init() {
                                    this.on('addedfiles', function (files, xhr, formData) {
                                      const added = acceptedDropzoneFiles(files);
                                      added.forEach(file =>{
                                        file.entity = selectedIdentificationDocumentTypes[index] + '_2';
                                        file.dropzone_index = index;
                                      })
                                      addedFiles = [...added, ...addedFiles];
                                    });
                                    },
                                }" :previews-disabled="true" :minimal="true"/>
                                </div>
                                <div v-if="addedFiles?.length > 0" class="col-12 mb-3">
                                  <div v-for="file of addedFiles" class="row">
                                    <template v-if="file?.entity === selectedIdentificationDocumentTypes[index] + '_2'">
                                      <div class="col">
                                        <span>{{ file.name }}</span>
                                      </div>
                                      <div class="col-auto">
                                        <button type="button" class="btn btn-link btn-text-red pe-0" @click="removeFile(file)">{{ t('clientOnboardingOverlay.remove') }}</button>
                                      </div>
                                    </template>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div v-if="index === 0" class="col-12 d-block d-lg-none">
                          <hr class="my-3">
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-12 d-flex justify-content-between">
                      <button
                          type="button"
                          v-if="step"
                          class="btn btn-md ps-0"
                          @click="previousStep"
                      >
                        <span class="arrow-left-icon"></span>
                        <span>Zpět</span>
                      </button>
                      <button v-if="!bankIDIdentificationProcessing && selectedIdentificationMethod === 'DOCUMENT'" :disabled="!isIdentificationButtonEnabled" class="btn btn-primary btn-md" id="step-submit-button" type="button" @click="processIdentification">
                        <span>{{ t('clientOnboardingOverlay.confirm') }}</span>
                        <span class="arrow-right-icon"></span>
                      </button>
                      <button v-if="bankIDIdentificationProcessing && bankIDIdentificationDone" id="step-submit-button" class="btn btn-primary btn-md" type="button" @click="nextStep">
                        <span>{{ t('clientOnboardingOverlay.next_step') }}</span>
                        <span class="arrow-right-icon"></span>
                      </button>
                    </div>
                  </div>
                </template>
                <template v-if="step.type === 'meeting_records'">
                  <form @submit.prevent="processMeetingRecord" :key="step.id">
                    <div class="modal-body">
                      <DynamicForm :disabled="false" :config="step.data" ref="dynamicFormRef"></DynamicForm>
                    </div>

                    <div class="row">
                      <div class="col-12 d-flex justify-content-between">
                        <button
                            type="button"
                            v-if="step"
                            class="btn btn-md ps-0"
                            @click="previousStep"
                        >
                          <span class="arrow-left-icon"></span>
                          <span>{{ t('clientOnboardingOverlay.back') }}</span>
                        </button>
                        <button class="btn btn-primary btn-md" id="step-submit-button" type="submit">
                          <span>{{ t('clientOnboardingOverlay.continue') }}</span>
                          <span class="arrow-right-icon"></span>
                        </button>
                      </div>
                    </div>
                  </form>
                </template>
                <!-- IMA-487: AML dotazník jako samostatný krok (dřív součást aggregated_form) -->
                <template v-if="step.type === 'aml'">
                  <div v-if="!assistedOnboarding && step.advisor_only" class="row mb-3">
                    <div class="col-12 text-center">
                      {{ t('clientOnboardingOverlay.aml_advisor_only') }}
                    </div>
                  </div>
                  <form v-else @submit.prevent="processInteractionFormsStep" :key="step.id">
                    <div class="modal-body">
                      <DynamicForm :disabled="false" :config="amlVisibleStepData" ref="dynamicFormRef"></DynamicForm>
                    </div>

                    <div class="row">
                      <div class="col-12 d-flex justify-content-between">
                        <button
                            type="button"
                            v-if="step"
                            class="btn btn-md ps-0"
                            @click="previousStep"
                        >
                          <span class="arrow-left-icon"></span>
                          <span>{{ t('clientOnboardingOverlay.back') }}</span>
                        </button>
                        <div class="d-flex">
                          <button type="button" class="btn btn-outline-primary btn-md me-2 d-flex align-items-center justify-content-center" id="aml-show-result-button" :disabled="amlResultLoading" @click="openAmlQuestionnaireResultsModal()">
                            <span v-if="amlResultLoading" class="d-flex align-items-center">
                              <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              {{ t('clientOnboardingOverlay.aml_loading') }}
                            </span>
                            <span v-else>{{ t('clientOnboardingOverlay.aml_show_result') }}</span>
                          </button>
                          <button class="btn btn-primary btn-md" id="step-submit-button" type="submit">
                            <span>{{ t('clientOnboardingOverlay.continue') }}</span>
                            <span class="arrow-right-icon"></span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                </template>
                <template v-if="step.type === 'client_complex_form'">
                  <Form v-on:submit="processInteractionFormsStep">
                    <div class="row">
                      <div class="col-12 col-md-6 form-group">
                        <label for="firstnameInput" class="form-label required-label">{{ t('clientOnboardingOverlay.name') }}</label>
                        <Field v-model="step.data.firstname" id="firstnameInput" name="firstnameInput" class="form-control" rules="required" :placeholder="t('clientOnboardingOverlay.complex_form_your_firstname')"/>
                        <ErrorMessage name="firstnameInput"/>
                      </div>
                      <div class="col-12 col-md-6 form-group">
                        <label for="surnameInput" class="form-label required-label">{{ t('clientOnboardingOverlay.surname') }}</label>
                        <Field v-model="step.data.surname" id="surnameInput" name="surnameInput" class="form-control" rules="required" :placeholder="t('clientOnboardingOverlay.complex_form_your_surname')"/>
                        <ErrorMessage name="surnameInput"/>
                      </div>
                      <div class="col-12 col-md-6 form-group">
                        <label for="birthNumberInput" class="form-label required-label">{{ t('clientOnboardingOverlay.birth_number') }}</label>
                        <Field v-model="step.data.personal_id" id="birthNumberInput" name="birthNumberInput" class="form-control" rules="required" :placeholder="t('clientOnboardingOverlay.complex_form_your_personal_id')"/>
                        <ErrorMessage name="birthNumberInput"/>
                      </div>


                      <div class="col-12 col-md-6 form-group">
                        <label for="countryOriginInput" class="form-label required-label">{{ t('clientOnboardingOverlay.country_origin') }}</label>
                        <Field id="countryOriginInput" v-model="step.data.nationality" name="countryOriginInput" rules="required">
                          <Multiselect
                              v-model="step.data.nationality"
                              :options="countryOriginSelectOptions"
                              :canClear="false"
                              :hide-selected="false"
                              :placeholder="t('clientOnboardingOverlay.country_origin')"
                          />
                          <ErrorMessage name="countryOriginInput"/>
                        </Field>
                      </div>
                      <div class="col-12 col-md-6 form-group">
                        <label for="citizenshipInput" class="form-label required-label">{{ t('clientOnboardingOverlay.citizenship') }}</label>
                        <Field id="citizenshipInput" v-model="step.data.citizenship" name="citizenshipInput" rules="required">
                          <Multiselect
                              v-model="step.data.citizenship"
                              :options="citizenshipSelectOptions"
                              :canClear="false"
                              :hide-selected="false"
                              :placeholder="t('clientOnboardingOverlay.complex_form_your_citizenship')"
                          />
                          <ErrorMessage name="citizenshipInput"/>
                        </Field>
                      </div>
                      <div class="col-12 col-md-6 form-group mb-4">
                        <label for="taxResidenceInput" class="form-label required-label">{{ t('clientOnboardingOverlay.tax_residence') }}</label>
                        <Field id="taxResidenceInput" v-model="step.data.tax_residence" name="taxResidenceInput" rules="required">
                          <Multiselect
                              v-model="step.data.tax_residence"
                              :options="taxResidenceSelectOptions"
                              :canClear="true"
                              :hide-selected="false"
                              :placeholder="t('clientOnboardingOverlay.complex_form_your_tax_residence')"
                          />
                          <ErrorMessage name="taxResidenceInput"/>
                        </Field>
                      </div>
                      <div class="col-12 col-md-6 form-group mb-4">
                        <div class="form-check my-4">
                          <input v-model="step.data.not_US_tax_residence" id="not_US_tax_residence" type="checkbox" class="form-check-input"/>
                          <label for="not_US_tax_residence" class="form-check-label">{{ t('clientOnboardingOverlay.complex_form_not_us_tax_residence') }}</label>
                        </div>
                      </div>
                      <div v-if="locale === 'cs' ? step.data.tax_residence !== 'Česká republika' : step.data.tax_residence !== 'Slovenská republika'" class="col-12 col-md-6 form-group">
                        <label for="tinInput" class="form-label">{{ t('clientOnboardingOverlay.tin') }}</label>
                        <Field v-model="step.data.tax_number" id="tinInput" name="tinInput" class="form-control" :placeholder="t('clientOnboardingOverlay.complex_form_your_tin')"/>
                        <ErrorMessage name="tinInput"/>
                        <small v-if="false" class="text-muted">Budeme Vás kontaktovat a vyžadovat doklad.</small>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-12 d-flex justify-content-between">
                        <button
                            type="button"
                            v-if="step"
                            class="btn btn-md ps-0"
                            @click="previousStep"
                        >
                          <span class="arrow-left-icon"></span>
                          <span>Zpět</span>
                        </button>
                        <button v-if="step" class="btn btn-primary btn-md ms-4" id="step-submit-button" type="submit">
                          <span>{{ t('clientOnboardingOverlay.continue') }}</span>
                          <span class="arrow-right-icon"></span>
                        </button>
                      </div>
                    </div>
                  </Form>
                </template>
                <template v-if="step.type === 'address'">
                  <Form v-on:submit="processInteractionFormsStep">
                    <fieldset class="m-0 p-0 border-0">
                      <div class="mb-3"><strong>{{ t('clientOnboardingOverlay.permanent_address') }}</strong></div>
                      <div class="row">
                        <div class="col-12 col-md-6 form-group">
                          <label for="streetInput" class="form-label required-label">{{ t('clientOnboardingOverlay.street') }}</label>
                          <Field v-model="step.data.domicile_address.street" id="streetInput" name="streetInput" class="form-control" rules="required" :placeholder="t('clientOnboardingOverlay.street')"/>
                          <ErrorMessage name="streetInput"/>
                        </div>
                        <div class="col-12 col-md-6 form-group">
                          <label for="cityInput" class="form-label required-label">{{ t('clientOnboardingOverlay.city') }}</label>
                          <Field v-model="step.data.domicile_address.city" id="cityInput" name="cityInput" class="form-control" rules="required" :placeholder="t('clientOnboardingOverlay.city')"/>
                          <ErrorMessage name="cityInput"/>
                        </div>
                        <div class="col-12 col-md-6 form-group">
                          <label for="zipCodeInput" class="form-label required-label">{{ t('clientOnboardingOverlay.zip_code') }}</label>
                          <Field v-model="step.data.domicile_address.zip_code" id="zipCodeInput" name="zipCodeInput" class="form-control" rules="required" :placeholder="t('clientOnboardingOverlay.zip_code')"/>
                          <ErrorMessage name="zipCodeInput"/>
                        </div>
                        <div class="col-12 col-md-6 form-group">
                          <label for="countryInput" class="form-label required-label">{{ t('clientOnboardingOverlay.country') }}</label>
                          <Field id="countryInput" v-model="step.data.domicile_address.country" name="countryInput" rules="required">
                            <Multiselect
                                v-model="step.data.domicile_address.country"
                                :options="countryOriginSelectOptions"
                                :canClear="false"
                                :hide-selected="false"
                                :placeholder="t('clientOnboardingOverlay.country')"
                            />
                            <ErrorMessage name="countryInput"/>
                          </Field>
                        </div>
                      </div>

                      <div class="form-check my-4">
                        <input v-model="step.data.correspondence_not_the_same" id="correspondence_not_the_same" type="checkbox" class="form-check-input"/>
                        <label for="correspondence_not_the_same" class="form-check-label">{{ t('clientOnboardingOverlay.correspondence_address_not_the_same') }}</label>
                      </div>

                      <div v-if="step.data.correspondence_not_the_same">
                        <div class="mb-3"><strong>{{ t('clientOnboardingOverlay.correspondence_address') }}</strong></div>
                        <div class="row">
                          <div class="col-12 col-md-6 form-group">
                            <label for="streetInputCorrespondence" class="form-label required-label">{{ t('clientOnboardingOverlay.street') }}</label>
                            <Field v-model="step.data.correspondence_address.street" id="streetInputCorrespondence" name="streetInputCorrespondence" class="form-control" rules="required" :placeholder="t('clientOnboardingOverlay.street')"/>
                            <ErrorMessage name="streetInputCorrespondence"/>
                          </div>
                          <div class="col-12 col-md-6 form-group">
                            <label for="cityInputCorrespondence" class="form-label required-label">{{ t('clientOnboardingOverlay.city') }}</label>
                            <Field v-model="step.data.correspondence_address.city" id="cityInputCorrespondence" name="cityInputCorrespondence" class="form-control" rules="required" :placeholder="t('clientOnboardingOverlay.city')"/>
                            <ErrorMessage name="cityInputCorrespondence"/>
                          </div>
                          <div class="col-12 col-md-6 form-group">
                            <label for="zipCodeInputCorrespondence" class="form-label required-label">{{ t('clientOnboardingOverlay.zip_code') }}</label>
                            <Field v-model="step.data.correspondence_address.zip_code" id="zipCodeInputCorrespondence" name="zipCodeInputCorrespondence" class="form-control" rules="required" :placeholder="t('clientOnboardingOverlay.zip_code')"/>
                            <ErrorMessage name="zipCodeInputCorrespondence"/>
                          </div>
                          <div class="col-12 col-md-6 form-group">
                            <label for="countyInputCorrespondence" class="form-label required-label">{{ t('clientOnboardingOverlay.country') }}</label>
                            <Field id="countyInputCorrespondence" v-model="step.data.correspondence_address.country" name="countyInputCorrespondence" rules="required">
                              <Multiselect
                                  v-model="step.data.correspondence_address.country"
                                  :options="countryOriginSelectOptions"
                                  :canClear="false"
                                  :hide-selected="false"
                                  :placeholder="t('clientOnboardingOverlay.country')"
                              />
                              <ErrorMessage name="countyInputCorrespondence"/>
                            </Field>
                          </div>
                          <div class="col-12 form-group mb-3">
                            <label for="note" class="form-label required-label">{{ t('clientOnboardingOverlay.note_address') }}</label>
                            <Field v-model="step.data.correspondence_address.note" id="note" name="note" class="form-control" :placeholder="t('clientOnboardingOverlay.note_address')"/>
                          </div>
                        </div>
                      </div>

                      <div class="row">
                        <div class="col-12 d-flex justify-content-between">
                          <button
                              type="button"
                              v-if="step"
                              class="btn btn-md ps-0"
                              @click="previousStep"
                          >
                            <span class="arrow-left-icon"></span>
                            <span>{{ t('clientOnboardingOverlay.back') }}</span>
                          </button>
                          <button v-if="step" class="btn btn-primary btn-md ms-4" id="step-submit-button" type="submit">
                            <span>{{ t('clientOnboardingOverlay.continue') }}</span>
                            <span class="arrow-right-icon"></span>
                          </button>
                        </div>
                      </div>
                    </fieldset>
                  </Form>
                </template>
                <template v-if="step.type === 'bank_account'">
                  <Form v-on:submit="processInteractionFormsStep">
                    <div class="row">
                      <div class="col-12 form-group mb-3">
                        <div class="d-flex justify-content-between">
                          <label for="currencySelect" class="form-label">{{ t('saveClientBankAccountModal.currency') }}</label>
                          <div class="form-check">
                            <input @click="step.data.currencies = []" v-model="step.data.is_multicurrency" class="form-check-input" type="checkbox" value="" id="multicurrency">
                            <label class="form-check-label" for="multicurrency">
                              {{ t('saveClientBankAccountModal.multi_currency_account') }}
                            </label>
                          </div>
                        </div>
                        <Multiselect v-if="step.data.is_multicurrency" required mode="tags" v-model="step.data.currencies" :options="['CZK', 'EUR', 'USD']" :hide-selected="false" :placeholder="t('saveClientBankAccountModal.choose_currencies')"/>
                        <Multiselect v-else required mode="single" v-model="step.data.currencies[0]" :options="['CZK', 'EUR', 'USD']" :hide-selected="false" :placeholder="t('saveClientBankAccountModal.choose_currency')"/>
                      </div>
                      <div v-if="showBankAccount" class="col-12 col-md-6 form-group">
                        <label for="accountNumberInput" class="form-label required-label">{{ t('saveClientBankAccountModal.account_number') }}</label>
                        <Field v-model="step.data.account_number" id="accountNumberInput" name="accountNumberInput" class="form-control" required :placeholder="t('saveClientBankAccountModal.bank_account_number')"/>
                        <ErrorMessage name="accountNumberInput"/>
                      </div>
                      <div v-if="showBankAccount" class="col-12 col-md-6 form-group">
                        <label for="bankCodeInput" class="form-label required-label">{{ t('saveClientBankAccountModal.bank_code') }}</label>
                        <Field v-model="step.data.bank_code" id="bankCodeInput" name="bankCodeInput" class="form-control" required :placeholder="t('saveClientBankAccountModal.bank_code')"/>
                        <ErrorMessage name="bankCodeInput"/>
                      </div>
                      <div v-if="!showBankAccount" class="col-12 col-md-6 form-group">
                        <label for="ibanInput" class="form-label required-label">IBAN</label>
                        <Field v-model="step.data.iban" id="ibanInput" name="ibanInput" class="form-control" required placeholder="IBAN"/>
                        <ErrorMessage name="ibanInput"/>
                      </div>
                      <div v-if="!showBankAccount" class="col-12 col-md-6 form-group mb-3">
                        <label for="bicInput" class="form-label required-label">BIC/SWIFT</label>
                        <Field v-model="step.data.bic" id="bicInput" name="bicInput" class="form-control" required placeholder="BIC/SWIFT"/>
                        <ErrorMessage name="bicInput"/>
                      </div>
                    </div>
                    <div class="row mb-3">
                      <div class="col-12">
                        <div class="form-check">
                          <input v-model="step.data.declaration_owner" id="declaration_owner" type="checkbox" class="form-check-input" @change="setDeclarations('declaration_owner')"/>
                          <label for="declaration_owner" class="form-check-label">{{ t('clientOnboardingOverlay.declare_account_owner') }}</label>
                        </div>
                      </div>
                      <div class="col-12">
                        <div class="form-check">
                          <input v-model="step.data.declaration_spouses_property" id="declaration_spouses_property" type="checkbox" class="form-check-input" @change="setDeclarations('declaration_spouses_property')"/>
                          <label for="declaration_spouses_property" class="form-check-label">{{ t('clientOnboardingOverlay.declare_account_my_name') }}</label>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-12 d-flex justify-content-between">
                        <button
                            type="button"
                            v-if="step"
                            class="btn btn-md ps-0"
                            @click="previousStep"
                        >
                          <span class="arrow-left-icon"></span>
                          <span>{{ t('clientOnboardingOverlay.back') }}</span>
                        </button>
                        <button v-if="step" class="btn btn-primary btn-md ms-4" id="step-submit-button" type="submit">
                          <span>{{ t('clientOnboardingOverlay.continue') }}</span>
                          <span class="arrow-right-icon"></span>
                        </button>
                      </div>
                    </div>
                  </Form>
                </template>
                <template v-if="step.type === 'calculation'">
                  <div class="row">
                    <div class="col-12 mb-4 fw-bold"><small>{{ t('clientOnboardingOverlay.no_strategy_info_1') }}<span @click="openInvestmentQuestionnaireResultsModal(step)" class="text-decoration-underline">{{ t('clientOnboardingOverlay.no_strategy_info_2') }}</span>{{ t('clientOnboardingOverlay.no_strategy_info_3') }}</small><span @click="goToStepByID('start')" style="right: 20px; cursor: pointer" class="mdi mdi-arrow-right-bold"></span>
                    </div>
                    <div class="col-6 form-group mb-2">
                      <label for="portfolioNameInput" class="form-label">{{ t('clientOnboardingOverlay.calculation_name_your_portfolio') }}</label>
                      <Field v-model="step.data.portfolio_name" id="portfolioNameInput" name="portfolioNameInput" class="form-control" :placeholder="t('clientOnboardingOverlay.calculation_my_savings')"/>
                      <ErrorMessage name="portfolioNameInput"/>
                    </div>
                    <div class="col-6 mb-2 d-inline-block">
                      <div>
                        <label for="strategiesSelect" class="form-label">{{ t('clientOnboardingOverlay.strategy') }}</label>
                        <!-- Bidli tenant points to the SK portfolio page; CZ uses the default in-equity product page -->
                        <a :href="portfoliaUrl" target="_blank" :title="t('clientOnboardingOverlay.calculation_more_info_website')"><span class="mdi mdi-information-outline ms-2"></span></a></div>
                      <select v-model="selectedStrategy" class="form-select form-select-lg" @change="getPerformance(selectedStrategy)">
                        <option v-for="strategy in availableStrategies" :value="strategy">{{ strategy.strategy_name }}</option>
                      </select>
                    </div>
                  </div>
                  <div class="row mb-4">
                    <div class="col-12">
                      <small class="text-muted">
                        {{ selectedStrategy?.description }}
                      </small>
                    </div>
                  </div>
                  <div class="calculation">
                    <div class="row mb-5">
                      <div class="col-12">
                        <div class="btn-group w-100" role="group" aria-label="Radio switch mode">
                          <input v-model="step.data.mode" @change="onModeChange()" type="radio" class="btn-check" name="options" id="option1" autocomplete="off" value="TARGET">
                          <label class="btn btn-outline-primary" for="option1">{{ t('clientOnboardingOverlay.calculation_your_investment_goal') }}
                            <BSTooltip :title="t('clientOnboardingOverlay.calculation_your_investment_goal_tooltip')">
                              <i class="mdi mdi-information-outline ms-2"/>
                            </BSTooltip>
                          </label>

                          <input v-model="step.data.mode" @change="onModeChange()" type="radio" class="btn-check" name="options" id="option2" autocomplete="off" value="SAVING">
                          <label class="btn btn-outline-primary" for="option2">{{ t('clientOnboardingOverlay.calculation_your_investment_plan') }}
                            <BSTooltip :title="t('clientOnboardingOverlay.calculation_your_investment_plan_tooltip')">
                              <i class="mdi mdi-information-outline ms-2"/></BSTooltip>
                          </label>
                        </div>
                      </div>
                    </div>
                    <div class="row mb-3">
                      <div class="col-5">
                        <div class="form-label">{{ t('clientOnboardingOverlay.currency') }}</div>
                        <div class="d-inline-flex ms-0 w-100">
                          <div class="form-check">
                            <input v-model="step.data.parameters.general.currency" @change="setCurrency(); calculationUpdated()" type="radio" class="btn-check-input" name="currency" id="currencyCzk" autocomplete="off" value="CZK">
                            <label class="form-check-label ms-2" for="currencyCzk">CZK
                            </label>
                          </div>
                          <div class="form-check">
                            <input v-model="step.data.parameters.general.currency" @change="setCurrency(); calculationUpdated()" type="radio" class="btn-check-input" name="currency" id="currencyEur" autocomplete="off" value="EUR">
                            <label class="form-check-label ms-2" for="currencyEur">EUR
                            </label></div>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-5">
                        <div v-if="step.data.mode === 'TARGET'">
                          <div class="col-12 form-group mb-5">
                            <label class="form-label">{{ t('clientOnboardingOverlay.calculation_how_much_you_want_saved') }}</label>
                            <CurrencyInput
                              v-model="step.data.parameters.target.amount"
                              :currency="step.data.parameters.general.currency"
                              :min="investmentGoalMin"
                              :max="300000000"
                              @blur="calculationUpdated"
                            />
                          </div>
                          <div class="col-12 form-group mb-5">
                            <label class="form-label">{{ t('clientOnboardingOverlay.calculation_how_long_you_want_invest') }}</label>
                            <vue-slider @change="calculationUpdated()" v-model="step.data.parameters.general.duration" :interval="1" :min="1" :max="50" :hide-label="true" :marks="false"/>
                            <div class="vue-slider-result">
                              <span class="vue-slider-result-label">{{ t('clientOnboardingOverlay.calculation_investment_horizon_is') }}:</span>
                              <span class="vue-slider-result-value">{{ formatYearsString(step.data.parameters.general.duration) }}</span>
                            </div>
                            <div class="col-12 form-group mb-5">
                              <label class="form-label">{{ t('clientOnboardingOverlay.calculation_recommended_monthly_investment') }}:</label>
                              <div class="vue-slider-result">
                                <span class="vue-slider-result-value">{{ formatAmount(monthlyInvestment, step.data.parameters.general.currency, 0) }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div v-if="step.data.mode === 'SAVING'">
                          <div class="col-12 form-group mb-5">
                            <label class="form-label">{{ t('clientOnboardingOverlay.calculation_how_long_you_want_invest') }}</label>
                            <vue-slider @change="calculationUpdated()" v-model="step.data.parameters.general.duration" :interval="1" :min="3" :max="60" :hide-label="true" :marks="false"/>
                            <div class="vue-slider-result">
                              <span class="vue-slider-result-label">{{ t('clientOnboardingOverlay.calculation_investment_horizon_is') }}:</span>
                              <span class="vue-slider-result-value">{{ formatYearsString(step.data.parameters.general.duration) }}</span>
                            </div>
                          </div>
                          <div class="col-12 form-group mb-5">
                            <label class="form-label">{{ t('clientOnboardingOverlay.calculation_initial_deposit_amount') }}</label>
                            <CurrencyInput
                              v-model="step.data.parameters.saving.initial_deposit"
                              :currency="step.data.parameters.general.currency"
                              :min="currentCurrencySettings?.initial_deposit_min"
                              :max="currentCurrencySettings?.initial_deposit_max"
                              @blur="calculationUpdated"
                            />
                          </div>
                          <div class="col-12 form-group mb-5">
                            <label class="form-label">{{ t('clientOnboardingOverlay.calculation_regular_deposit_amount') }}</label>
                            <CurrencyInput
                              v-model="step.data.parameters.saving.regular_deposit"
                              :currency="step.data.parameters.general.currency"
                              :min="currentCurrencySettings?.regular_deposit_min"
                              :max="currentCurrencySettings?.regular_deposit_max"
                              @blur="calculationUpdated"
                            />
                          </div>
                        </div>
                      </div>
                      <div class="col-7">
                        <LineChart
                            :digits="0"
                            :series="calculationChartDataSeries"
                            :valuesType="'amount'"
                            :categoriesType="'years'"
                            :categories="calculationChartDataCategories"
                            :currency="currency"
                        ></LineChart>

                      </div>
                    </div>
                    <div class="row mt-4">
                      <div class="col-4 form-group mb-5">
                        <div class="card card--big-value card--ns">
                          <div class="card-body">
                            <!-- Bidli tenant points to the SK portfolio page; CZ uses the default in-equity product page -->
                            <a style="position: absolute; right: 10px; top: 10px" :href="portfoliaUrl" target="_blank" :title="t('clientOnboardingOverlay.calculation_expected_return_info')"><span class="mdi mdi-information-outline ms-2"></span></a>
                            <div class="card__value__label">{{ t('clientOnboardingOverlay.calculation_expected_return') }}</div>
                            <div class="card__value">
                              {{ formatPercents(performance * 100) }}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="col-4 form-group mb-4">
                        <div class="card card--big-value card--ns">
                          <div class="card-body">
                            <div class="card__value__label">{{ t('clientOnboardingOverlay.calculation_expected_value_end') }}</div>
                            <div class="card__value" v-if="finalInvestmentValue">
                              {{ formatAmount(finalInvestmentValue, step.data.parameters.general.currency, 0) }}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="col-4 form-group mb-4">
                        <div class="card card--big-value card--ns">
                          <div class="card-body">
                            <div class="card__value__label">{{ t('clientOnboardingOverlay.calculation_total_invested') }}</div>
                            <div class="card__value" v-if="totalInvestedAmount">
                              {{ formatAmount(totalInvestedAmount, step.data.parameters.general.currency, 0) }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div v-if="meetingRecordModified" class="row mb-2">
                      <div class="col-12">
                        <div class="alert alert-warning py-2 mb-0 small">
                          {{ t('clientOnboardingOverlay.calculation_meeting_record_locked_warning') }}
                        </div>
                      </div>
                    </div>
                    <div class="row mb-3">
                      <div class="col-12">
                        <small>{{ generateCalcSummaryText() }}</small>
                        <small v-if="!assistedOnboarding">{{ step.data.summary_text }}</small>
                      </div>
                      <div v-if="assistedOnboarding" class="col-12 form-group d-none">
                        <label for="summary_text" class="form-label">{{ t('clientOnboardingOverlay.calculation_summary') }}</label>
                        <textarea type="text" v-model="step.data.summary_text" class="form-control" id="summary_text" rows="3"></textarea>
                      </div>
                    </div>

                    <div class="row">
                      <div class="col-12 d-flex justify-content-between">
                        <button
                            type="button"
                            v-if="step"
                            class="btn btn-md ps-0"
                            @click="previousStep"
                        >
                          <span class="arrow-left-icon"></span>
                          <span>{{ t('clientOnboardingOverlay.back') }}</span>
                        </button>
                        <button v-if="step" class="btn btn-primary btn-md ms-4" id="step-submit-button" type="button" @click="processCalculationStep">
                          <span>{{ t('clientOnboardingOverlay.continue') }}</span>
                          <span class="arrow-right-icon"></span>
                        </button>
                      </div>
                    </div>
                    <div class="row mt-4">
                      <div class="col-12">
                        <small class="text-muted">
                          {{ t('clientOnboardingOverlay.calculation_bottom_info_text_1') }}

                          {{ t('clientOnboardingOverlay.calculation_bottom_info_text_2') }}

                          {{ t('clientOnboardingOverlay.calculation_bottom_info_text_3') }}
                        </small>
                      </div>
                    </div>
                  </div>
                </template>
                <template v-if="false && step.type === 'strategies'">
                  <Form v-on:submit="processStrategies">
                    <div class="row mb-3">
                      <div class="col-12">
                        {{ t('clientOnboardingOverlay.calculation_select_strategies') }}
                      </div>
                    </div>

                    <div class="row">
                      <div class="col-12">
                        <div v-for="strategy of step.available_values" class="form-check strategy-check mb-3" :class="strategy.checked ? 'checked' : ''">
                          <input v-model="strategy.checked" :id="strategy.id" type="checkbox" class="form-check-input"/>
                          <label :for="strategy.id" class="form-check-label">{{ strategy.name }}</label>
                        </div>
                      </div>
                    </div>

                    <div class="row mt-3">
                      <div class="col-12 d-flex justify-content-between">
                        <button
                            type="button"
                            v-if="step"
                            class="btn btn-md ps-0"
                            @click="previousStep"
                        >
                          <span class="arrow-left-icon"></span>
                          <span>{{ t('clientOnboardingOverlay.back') }}</span>
                        </button>
                        <button v-if="step" class="btn btn-primary btn-md ms-4" id="step-submit-button" type="submit">
                          <span>{{ t('clientOnboardingOverlay.continue') }}</span>
                          <span class="arrow-right-icon"></span>
                        </button>
                      </div>
                    </div>
                  </Form>
                </template>
                <template v-if="step.type === 'documents'">
                  <div class="row mb-3">
                    <div class="col-12">
                      {{ t('clientOnboardingOverlay.sign_documents') }}
                    </div>
                  </div>
                  <DocumentsListMinimalComponent :isNewOnboarding="true"></DocumentsListMinimalComponent>
                  <div class="row mt-3">
                    <div class="col-12 d-flex justify-content-between">
                      <button
                          type="button"
                          v-if="step"
                          class="btn btn-md ps-0"
                          @click="previousStep"
                      >
                        <span class="arrow-left-icon"></span>
                        <span>{{ t('clientOnboardingOverlay.back') }}</span>
                      </button>
                      <button @click="processInteractionFormsStep" v-if="step" class="btn btn-primary btn-md ms-4" id="step-submit-button" type="button">
                        <span>{{ t('clientOnboardingOverlay.continue') }}</span>
                        <span class="arrow-right-icon"></span>
                      </button>
                    </div>
                  </div>
                </template>
                <template v-if="step.type === 'summary'">
                  <form v-if="!showAnotherInformation">
                    <div v-if="!assistedOnboarding && uncompletedSteps.length > 0" class="row">
                      <div class="col-12">
                        <div class="alert alert-warning">
                          <div v-for="step of steps" class="row">
                            <div v-if="step.state !== 'COMPLETED' && !['start', 'end', 'summary'].includes(step.type)" class="col-12">{{ t('clientOnboardingOverlay.step_not_completed') }} {{ step?.title }} <span class="mdi mdi-pencil cursor-pointer ms-2" @click="goToStepByID(step.id, true)"></span></div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-12">
                        <div class="summary-info">
                          <div class="row">
                            <template v-for="summaryDataSection in summaryDataOrder">
                              <template v-for="step of step.data.summary">
                                <div v-if="summaryDataSection === step.key" class="col-6 mb-3">
                                  <div class="summary-info-item position-relative">
                                    <span @click="goToStepByID(step.key, true)" style="position: absolute; right: 20px; cursor: pointer" class="mdi mdi-pencil mdi-24px"></span>
                                    <div v-if="step?.title" class="summary-info-item-title">{{ step?.title }}</div>
                                    <div class="summary-info-item-content">
                                      <span class="summary-info-item-content-item" v-if="typeof step.value === 'string'">{{ step?.title }}: <strong>{{ step.value }}</strong></span>
                                      <div v-else>
                                        <div v-for="value of step.value">
                                          <span class="summary-info-item-content-item" v-if="typeof value === 'string'">{{ value }}</span>
                                          <div v-else>
                                            <span class="summary-info-item-content-item" v-if="typeof value.value === 'string'">{{ value.label }}: <strong>{{ value.value }}</strong></span>
                                            <div v-else>
                                              <div class="summary-info-item-subtitle mb-1">{{ value.label }}</div>
                                              <div v-for="subValue of value.value">
                                                <span class="summary-info-item-content-item" v-if="typeof subValue.value === 'string'">{{ subValue.label }}: <strong>{{ subValue.value }}</strong></span>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </template>
                            </template>
                          </div>
                        </div>
                      </div>

                      <div class="col-12 mb-3 d-flex align-items-center">
                        <input v-model="step.data.terms_agree" disabled id="terms_agree" type="checkbox" style="opacity: 1" class="form-check-input form-check-input-bigger me-2" required @change="handleVerificationShow"/>
                        <label style="opacity: 1" for="terms_agree" class="form-check-label required-label">
                        <span>
                          {{ t('clientOnboardingOverlay.agree_with') }} <span v-for="(confirmDocument, index) in step.data.confirm_documents" v-bind:key="confirmDocument">
                            {{ (index === 0 ? '' : ', ') }}
                            <a class="cursor-pointer text-decoration-underline" v-if="confirmDocument.document_id" @click="openDocumentDetailModal(confirmDocument.document_id)">{{ confirmDocument.label }}</a>
                            <a v-else target="_blank" :href="confirmDocument.link">{{ confirmDocument.label }}</a>
                          </span>
                        </span>
                        </label>
                      </div>

                      <!-- IMA-486: přílohy k onboardingu - nahrává/odebírá/vidí poradce/back office/admin
                           přímo v dropzone (manuallyAddFile), klient jen vidí odkazy na soubory -->
                      <div v-if="assistedOnboarding" class="col-12 mb-3">
                        <div class="form-check">
                          <input v-model="showAddAttachment" id="want_to_add_attachment" type="checkbox" class="form-check-input"/>
                          <label for="want_to_add_attachment" class="form-check-label">{{ t('clientOnboardingOverlay.want_to_add_attachment') }}</label>
                        </div>
                        <!-- paramName má "[]" i v single-file režimu (uploadMultiple:false) -
                             bez něj PHP/Laravel nerozparsuje pole jako array a validace selže s 422 -->
                        <CustomDropzone v-if="showAddAttachment" ref="attachmentsDropzoneRef" :dropzone-options-input="{
                          url: getApiBaseUrl() + '/api/documents/upload',
                          acceptedFiles: '.pdf, .jpg, .jpeg, .png',
                          maxFiles: 100,
                          uploadMultiple: false,
                          parallelUploads: 3,
                          paramName: 'attachments[]',
                          params: {
                            documentable_type: 'App\\Models\\InteractionForm',
                            documentable_id: interactionFormsID
                          },
                          init() {
                            this.on('sending', function (file, xhr, formData) {
                              formData.append('documentable_type', 'App\\Models\\InteractionForm');
                              formData.append('documentable_id', interactionFormsID);
                            });
                          }
                        }" :upload-success="onAttachmentUploaded" :on-remove-file="onAttachmentRemovedFromDropzone"/>
                      </div>
                      <div v-else-if="interactionFormAttachments.length" class="col-12 mb-3">
                        <div class="fw-bold mb-1">{{ t('clientOnboardingOverlay.additional_attachments') }}</div>
                        <div v-for="attachment in interactionFormAttachments" :key="attachment.id">
                          <a class="cursor-pointer text-decoration-underline" @click="openDocumentDetailModal(attachment.id)">{{ attachment.name }}</a>
                        </div>
                      </div>

                      <!-- IMA-500: "Podepsat další dokumenty" - jen poradce/back office (assistedOnboarding),
                           jen pokud to nastavení dané kategorie ASO povoluje (additionalDocumentsEnabled),
                           a jen pokud má klient vůbec nějaký nepodepsaný formulář k výběru - jinak by volba
                           byla vždy prázdná a matoucí.
                           Vybrané formuláře se odešlou v datech kroku summary jako additional_form_ids. -->
                      <div v-if="assistedOnboarding && additionalDocumentsEnabled && (availableWaitingFormsLoading || availableWaitingFormsOptions.length > 0)" class="col-12 mb-3">
                        <div class="form-check">
                          <input v-model="showAddForms" id="want_to_add_forms" type="checkbox" class="form-check-input"/>
                          <label for="want_to_add_forms" class="form-check-label">{{ t('clientOnboardingOverlay.want_to_add_forms') }}</label>
                        </div>
                        <div v-if="showAddForms">
                          <Multiselect
                              v-model="step.data.additional_form_ids"
                              :options="availableWaitingFormsOptions"
                              :loading="availableWaitingFormsLoading"
                              :close-on-select="false"
                              :hide-selected="false"
                              :placeholder="t('clientOnboardingOverlay.select_forms_to_sign')"
                              mode="tags"
                          />
                        </div>
                      </div>

                      <div class="col-6">
                        <button
                            type="button"
                            class="btn btn-md ps-0"
                            @click="startAgain()"
                        >
                          <span class="arrow-left-icon"></span>
                          <span>{{ t('clientOnboardingOverlay.start_again') }}</span>
                        </button>
                      </div>
                      <div v-if="loggedUser.client" class="col-6 text-end">
                        <button class="btn btn-primary btn-md" type="button" @click="handleVerificationShow" :disabled="uncompletedSteps.length">
                          <span>{{ t('clientOnboardingOverlay.sign') }}</span>
                        </button>
                      </div>
                    </div>
                  </form>
                  <div v-if="showVerification && !showAnotherInformation" class="mb-5 sms-verification" ref="verificationForm">
                    <form @submit.prevent="processConfirmation(step)">
                      <div class="form-group">
                        <div class="form-label d-flex justify-content-between">
                          <label class="" for="2fa_code_sms"><span>SMS kód</span><br/><small>{{ t('clientOnboardingOverlay.summary_code_sent_1') }} {{ maskPhoneNumber(step.data.phone) }} {{ t('clientOnboardingOverlay.summary_code_sent_2') }}</small></label>
                        </div>

                        <input id="2fa_code_sms" class="form-control"
                               type="text" placeholder="" autocomplete="off"
                               v-model="step.data.verification_code"/>
                        <div class="mt-2">
                            <span v-if="codeSentLoading"><span class="spinner-border spinner-border-sm" role="status">
                              <span class="visually-hidden">{{ t('dashboardClient.loadingMain') }}</span>
                            </span></span>
                          <a class="cursor-pointer" @click="generateCode" v-if="codeRequestInterval === 0 && !codeSentLoading"><small>{{ t('login.send_code') }} </small> <small v-if="codeSent"> {{ t('login.again') }} </small> </a>
                          <small class="text-muted" v-if="codeRequestInterval > 0">{{ t('login.auth_code_sent') }} {{ codeRequestInterval }}{{ t('login.secs') }}</small>
                        </div>
                      </div>
                      <div class="alert alert-danger mt-3" v-if="errorMessage"> {{ errorMessage }}</div>
                      <div class="row mt-3">
                        <div class="col-12 d-flex justify-content-between">
                          <button
                              type="button"
                              v-if="step"
                              class="btn btn-md ps-0"
                              @click="previousStep"
                          >
                            <span class="arrow-left-icon"></span>
                            <span>{{ t('clientOnboardingOverlay.back') }}</span>
                          </button>
                          <button :disabled="!step.data.verification_code" v-if="step" id="step-submit-button" class="btn btn-primary btn-md">
                            <span>{{ t('clientSelfRegister.continue') }}</span>
                            <span class="arrow-right-icon"></span>
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                  <div v-if="showAnotherInformation">
                    <div class="row mb-2">
                      <div class="col-12 text-center">
                        <div class="logo" v-if="tenantTheme && tenantTheme.logoBase64">
                          <img style="max-width: 200px" :src="tenantTheme.logoBase64" v-if="tenantTheme && tenantTheme.logoBase64" alt=""/>
                        </div>
                      </div>
                      <h3 class="col-12 mt-4 mb-5 text-center">
                        {{ t('clientOnboardingOverlay.end_investment_account_created') }}
                      </h3>
                    </div>
                    <div v-for="paymentInfo of paymentInformations" class="row mt-2 mb-4">
                      <div class="col-6 d-flex justify-content-end">
                        <div class="qr-code-wrapper mb-3">
                          <img :src="paymentInfo.payment_qr_code_base64" alt="QR-code" style="width: 200px;">
                        </div>
                      </div>
                      <div class="col-6">
                        <div>
                          <span class="fw-bold"><span v-if="paymentInfo.currency === 'CZK'">{{ t('clientOnboardingOverlay.account_number') }}: </span><span v-else>IBAN: </span></span><span>{{ paymentInfo.bank_account_number_full }}</span>
                        </div>
                        <div v-if="paymentInfo.currency !== 'CZK'">
                          <span class="fw-bold">BIC/SWIFT: </span><span>{{ paymentInfo.bic_swift_code }}</span>
                        </div>
                        <div>
                          <span v-if="paymentInfo.currency === 'CZK'" class="fw-bold">{{ t('clientOnboardingOverlay.variable_symbol') }}: </span><span v-else class="fw-bold">Reference platby: </span><span>{{ paymentInfo.variable_symbol }}</span>
                        </div>
                        <div class="mt-2">
                          <span class="fw-bold">{{ t('clientOnboardingOverlay.strategy_name') }}: </span><span>{{ paymentInfo.strategy_name }}</span>
                        </div>
                        <div>
                          <span class="fw-bold">{{ t('clientOnboardingOverlay.investment_horizon') }}: </span><span>{{ paymentInfo.meta.investment_horizont }}</span>
                        </div>
                        <div>
                          <span class="fw-bold">{{ t('clientOnboardingOverlay.initial_investment') }}: </span><span>{{ paymentInfo.meta.initial_deposit }}</span>
                        </div>
                        <div>
                          <span class="fw-bold">{{ t('clientOnboardingOverlay.regular_investment') }}: </span><span>{{ paymentInfo.meta.regular_deposit }}</span>
                        </div>
                      </div>
                    </div>
                    <div v-if="!isBidli" class="row mt-5 mb-3">
                      <div class="col-12 d-flex justify-content-center">
                        <span class="download-apps-title">{{ t('clientOnboardingOverlay.end_text') }}</span>
                      </div>
                    </div>
                    <div class="row mt-4">
                      <div v-if="!isBidli" class="col-6 ">
                        <div class="app-badge d-flex justify-content-end">
                          <a target="_blank" href="https://play.google.com/store/apps/details?id=cz.inequity.app">
                            <img src="@/assets/img/GooglePlay_Badge_CZ.png" alt="Google Play Badge">
                          </a>
                        </div>
                      </div>
                      <div v-if="!isBidli" class="col-6">
                        <div class="app-badge d-flex justify-content-start">
                          <a target="_blank" href="https://apps.apple.com/cz/app/in-equity/id6736932052">
                            <img src="@/assets/img/AppStore_Badge_CZ.png" alt="App Store Badge">
                          </a>
                        </div>
                      </div>
                      <div class="col-12 text-center">
                        <button class="btn btn-link btn-lg mt-5" type="button" @click="endOnboarding">{{ t('clientOnboardingOverlay.end_go_to_app') }}</button>
                      </div>
                    </div>
                  </div>
                </template>
                <template v-if="step.type === 'confirmation'">
                  <div class="row mb-3 justify-content-center">
                    <div class="col-md-8">
                      <form @submit.prevent="processConfirmation">
                        <div class="form-group">
                          <div class="text-center mb-3">{{ t('login.enter_auth_code') }}</div>
                          <label class="form-label" for="2fa_code">{{ t('login.phone_number2') }}</label>
                          <input id="2fa_code" class="form-control"
                                 type="text" placeholder="" autocomplete="off" readonly disabled
                                 :value="maskPhoneNumber(step.data.phone)" required/>
                        </div>
                        <div class="form-group">
                          <div class="form-label d-flex justify-content-between">
                            <label class="required-label" for="2fa_code">{{ t('login.auth_code') }}</label>
                          </div>
                          <input id="2fa_code" class="form-control"
                                 type="text" placeholder="" autocomplete="off"
                                 v-model="step.data.verification_code" required/>
                          <div class="mt-2">
                            <span v-if="codeSentLoading" class="spinner-border spinner-border-sm" role="status">
                              <span class="visually-hidden">{{ t('dashboardClient.loadingMain') }}</span>
                            </span>
                            <a class="cursor-pointer" @click="generateCode" v-if="codeRequestInterval === 0 && !codeSentLoading"><span>{{ t('login.send_code') }} </span> <span v-if="codeSent"> {{ t('login.again') }} </span> </a>
                            <span class="text-muted" v-if="codeRequestInterval > 0">{{ t('login.auth_code_sent') }} {{ codeRequestInterval }}{{ t('login.secs') }}</span>
                          </div>
                        </div>
                        <div class="alert alert-danger mt-3" v-if="errorMessage"> {{ errorMessage }}</div>
                      </form>
                    </div>
                  </div>
                </template>
                <template v-if="step.type === 'end'">
                  <div class="row mb-3">
                    <div class="col-12 text-center">
                      <i class="mdi mdi-check icon-primary icon-big"></i>
                    </div>
                    <h3 class="col-12 mt-5 mb-5 text-center">
                      {{ t('saveClientBankAccountModal.end_investment_account_created') }}
                    </h3>
                  </div>
                  <div class="row">
                    <div class="col-6">
                      <span class="fw-bold">{{ t('saveClientBankAccountModal.account_number') }}: </span><span>{{ step.data.payment - information.accountNumber }}</span>
                    </div>
                    <div class="col-6">

                    </div>
                    <div class="col-12 text-center">
                      <router-link to="/payment-terms" class="btn btn-primary btn-lg" type="button" @click="endOnboarding">{{ t('saveClientBankAccountModal.end_text_want_deposit') }}</router-link>
                      <br/>
                      <button class="btn btn-link btn-lg mt-5" type="button" @click="endOnboarding">{{ t('saveClientBankAccountModal.end_go_to_app') }}</button>
                    </div>
                  </div>
                </template>
                <div v-if="assistedOnboarding && !['end', 'start', 'confirmation'].includes(step.type)" class="row my-3">
                  <div class="col-12 d-flex justify-content-end">
                    <button
                        type="button"
                        v-if="step && step.type !== 'summary' && !step.advisor_only"
                        class="btn btn-md ps-0"
                        @click="skipStep"
                    >
                      <span>{{ t('clientOnboardingOverlay.skip') }}</span>
                      <span class="arrow-right-icon"></span>
                    </button>
                    <button v-if="step"
                            class="btn btn-md ms-4"
                            @click="saveAsConcept()"
                            type="button">
                      <span>{{ t('clientOnboardingOverlay.save_as_concept') }}</span>
                    </button>
                    <button v-if="step && step.type === 'summary'"
                            class="btn btn-primary btn-md ms-4"
                            @click="changeState('WAITING_FOR_CLIENT')"
                            type="button">
                      <span>{{ t('clientOnboardingOverlay.send_to_client') }}</span>
                    </button>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
    <OnboardingFooter></OnboardingFooter>
  </div>
</template>

<style scoped>
.arrow-right-icon {
  margin-left: 5px;
  line-height: 18px;
  font-size: 18px;
}

.arrow-right-icon::before {
  content: "\f0054";
  font: normal normal normal 18px / 1 "Material Design Icons";
}

.arrow-left-icon {
  margin-right: 5px;
  line-height: 18px;
  font-size: 18px;
}

.arrow-left-icon::before {
  content: "\F004D";
  font: normal normal normal 18px / 1 "Material Design Icons";
}

.strategy-check {
  position: relative;
  cursor: pointer;
  border: 1px solid var(--primaryColor);
  border-radius: 8px;
  display: flex;
  align-items: center;
  padding: 0;

  input.form-check-input:focus {
    box-shadow: none;
  }

  input.form-check-input {
    left: 15px;
    cursor: pointer;
    position: absolute;
    margin: 0;
    margin-right: 10px;
  }

  label.form-check-label {
    cursor: pointer;
    flex: 1;
    padding: 15px 15px 15px 45px;
  }
}

.strategy-check.checked {
  background: var(--primaryHoverColor);
  color: white;
}

h3 {
  margin: 20px 0 10px 0;
}

h5 {
  margin: 14px 0 6px 0;
}

p {
  margin-bottom: 3px;
}

.card {
  border-radius: 15px;

  .card-header {
    background: rgb(249 250 251);
  }
}

.progress {
  height: 10px;
}

@media screen and (max-width: 880px) {
  .btn.btn-primary.btn-md {
    min-width: 130px !important;
  }
}

.sms-verification small {
  font-size: 12px !important;
  color: var(--bs-secondary-color) !important;
  font-weight: 400 !important;
}

input[type="checkbox"].form-check-input-bigger {
  width: 25px;
  height: 25px;
}

.btn-outline-primary {
  color: var(--bs-primary-color);
}
</style>
